/**
 * Admin resident profile (360° view).
 *
 * Reached from /residents (resident name → Link). Shows the resident's
 * personal info + the full operational footprint we have on file: family,
 * co-residents on the same flat, accounts (billed/paid/outstanding), recent
 * bills, pendings emphasis, recent visitors, helpers (FV) with kind filter,
 * vehicles + gate entries, tenancy lease, amenity bookings, complaints,
 * move-in/out timeline, community activity, notices, and a Requests roll-up.
 *
 * Everything except the resident row + co-residents is loaded as a parallel
 * TanStack Query call against the existing list endpoints — keeps this page
 * cheap and removes any "god endpoint" temptation in the API. Where an
 * endpoint doesn't accept the filter we need (notices by unit, community
 * posts by author, vehicle-entries by unit, visitors by host, erasure by
 * resident) we pull the first N rows and client-filter — code comments
 * mark each spot.
 */
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import {
  AlertTriangle,
  ArrowLeft,
  Bell,
  CalendarDays,
  CalendarClock,
  Car,
  CheckCircle2,
  ClipboardList,
  Coins,
  Download,
  FileSignature,
  Globe,
  Home,
  ContactRound,
  Inbox,
  KeyRound,
  LogIn,
  Mail,
  MessageSquare,
  NotebookPen,
  Pencil,
  Phone,
  Receipt,
  Shield,
  ShieldAlert,
  ShieldCheck,
  Siren,
  Sparkles,
  Tag,
  Ticket,
  Trash2,
  Upload,
  UserCircle2,
  Users,
  UserSquare2,
  Vote as VoteIcon,
  Wallet,
  Wrench
} from 'lucide-react';
import { api, del, get, getErrorMessage, patch, post } from '@/lib/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import { Badge } from '@/components/ui/badge';
import { TabsPills } from '@/components/ui/tabs-pills';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { formatDate, formatDateTime } from '@/lib/format';
import { toast } from 'sonner';

// ─── types ────────────────────────────────────────────────────────────

type Relation = 'owner' | 'tenant' | 'family' | 'paying_guest';
type ComplaintStatus = 'open' | 'assigned' | 'in_progress' | 'resolved' | 'closed' | 'rejected';
type VisitorStatus = 'pending' | 'approved' | 'denied' | 'checked_in' | 'checked_out' | 'expired';
type BillStatus = 'unpaid' | 'paid' | 'overdue' | 'waived';
type ApprovalStatus = 'auto_approved' | 'pending_resident' | 'approved' | 'rejected';

interface CoResident {
  id: string;
  full_name: string;
  phone: string;
  relation: Relation;
  is_primary: boolean;
  lives_in_unit: boolean;
}

interface ResidentDetail {
  id: string;
  society_id: string;
  unit_id: string;
  full_name: string;
  phone: string;
  email: string | null;
  relation: Relation;
  is_primary: boolean;
  lives_in_unit: boolean;
  move_in_date: string | null;
  move_out_date: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  unit: {
    id: string;
    number: string;
    block: { id: string; name: string } | null;
  } | null;
  family_count: number;
  co_residents: CoResident[];
}

interface FamilyMember {
  id: string;
  full_name: string;
  relation: string;
  phone: string | null;
  date_of_birth: string | null;
}

interface FamilyListResp {
  items: FamilyMember[];
  next_cursor: string | null;
}

interface VisitorRow {
  id: string;
  visitor_name: string;
  phone: string;
  visit_kind: string;
  status: VisitorStatus;
  expected_at: string;
  created_at: string;
}

interface VisitorListResp {
  items: VisitorRow[];
  next_cursor: string | null;
}

interface BillRow {
  id: string;
  period_label: string;
  amount_inr: number;
  status: BillStatus;
  due_date: string;
  paid_at: string | null;
  late_fee_inr: number;
  late_fee_inr_computed?: number;
  total_due_inr?: number;
}

interface BillListResp {
  items: BillRow[];
}

interface ComplaintRow {
  id: string;
  title: string;
  category: string;
  status: ComplaintStatus;
  priority: string;
  created_at: string;
}

interface ComplaintListResp {
  items: ComplaintRow[];
  next_cursor: string | null;
}

interface FvAssignmentRow {
  id: string;
  active: boolean;
  days_of_week: string | null;
  time_start: string | null;
  time_end: string | null;
  approval_status: ApprovalStatus;
  frequent_visitor: {
    id: string;
    full_name: string;
    phone: string;
    kind: string;
    photo_url: string | null;
    status: string;
  };
}

interface FvAssignmentListResp {
  items: FvAssignmentRow[];
}

// ─── new types for T1 + T2 + Requests sections ───────────────────────

type VehicleKind = 'car' | 'bike' | 'cycle' | 'ev' | 'commercial' | 'other';
type VehicleEntryActor = 'resident' | 'visitor' | 'delivery' | 'service';
type VehicleEntryStatus = 'inside' | 'exited';

interface VehicleRow {
  id: string;
  number_plate: string;
  kind: VehicleKind;
  make_model: string | null;
  color: string | null;
  parking_slot: string | null;
  is_active: boolean;
  created_at: string;
}

interface VehicleListResp {
  items: VehicleRow[];
  next_cursor: string | null;
}

interface VehicleEntryRow {
  id: string;
  number_plate: string;
  actor_kind: VehicleEntryActor;
  status: VehicleEntryStatus;
  entered_at: string;
  exited_at: string | null;
  unit_id: string | null;
  visitor_name: string | null;
}

interface VehicleEntryListResp {
  items: VehicleEntryRow[];
  next_cursor: string | null;
}

type TransitionKind = 'move_in' | 'move_out' | 'tenant_swap';
type TransitionStatus =
  | 'draft'
  | 'notice_given'
  | 'handover_scheduled'
  | 'key_transferred'
  | 'noc_issued'
  | 'completed'
  | 'cancelled';

interface TransitionRow {
  id: string;
  unit_id: string;
  kind: TransitionKind;
  status: TransitionStatus;
  outgoing_resident_id: string | null;
  incoming_resident_id: string | null;
  outgoing_full_name: string | null;
  incoming_full_name: string | null;
  notice_date: string | null;
  effective_date: string | null;
  handover_date: string | null;
  key_transferred_at: string | null;
  completed_at: string | null;
  cancelled_at: string | null;
  lease_end_date: string | null;
  monthly_rent_inr: number | null;
  deposit_inr: number | null;
  created_at: string;
  nocs: Array<{
    id: string;
    noc_number: string;
    purpose: string;
    issued_at: string;
    revoked_at: string | null;
  }>;
}

interface TransitionListResp {
  items: TransitionRow[];
  next_cursor: string | null;
}

type NocPurpose =
  | 'move_out'
  | 'vehicle_registration'
  | 'passport'
  | 'electricity_connection'
  | 'gas_connection'
  | 'internet_connection'
  | 'general'
  | 'other';

interface NocRow {
  id: string;
  noc_number: string;
  purpose: NocPurpose;
  resident_full_name: string;
  issued_at: string;
  valid_until: string | null;
  revoked_at: string | null;
}

interface NocListResp {
  items: NocRow[];
  next_cursor: string | null;
}

type BookingStatus = 'pending' | 'confirmed' | 'cancelled' | 'completed' | 'no_show';
type BookingPaymentStatus = 'not_required' | 'pending' | 'paid' | 'refunded';

interface BookingRow {
  id: string;
  starts_at: string;
  ends_at: string;
  status: BookingStatus;
  payment_status: BookingPaymentStatus;
  fee_inr: number;
  guest_count: number;
  amenity: { id: string; name: string; kind: string; fee_inr: number };
}

interface BookingListResp {
  items: BookingRow[];
  next_cursor: string | null;
}

type NoticeAudience = 'all' | 'owners' | 'tenants' | 'block' | 'unit';
type NoticeStatus = 'draft' | 'published' | 'expired';
type NoticeCategory = 'general' | 'maintenance' | 'security' | 'event' | 'billing';

interface NoticeRow {
  id: string;
  title: string;
  body: string;
  category: NoticeCategory;
  status: NoticeStatus;
  pinned: boolean;
  audience: NoticeAudience;
  audience_block_id: string | null;
  audience_unit_id: string | null;
  published_at: string | null;
  expires_at: string | null;
  created_at: string;
  // Admin-shaped extras (resident has read flag instead — we're admin here).
  read_count?: number;
}

interface NoticeListResp {
  items: NoticeRow[];
  next_cursor: string | null;
}

interface CommunityPostRow {
  id: string;
  author_id: string;
  author_role: 'resident' | 'admin';
  category: string;
  body: string;
  like_count: number;
  comment_count: number;
  pinned: boolean;
  status: 'visible' | 'hidden' | 'deleted';
  created_at: string;
}

interface CommunityListResp {
  items: CommunityPostRow[];
  next_cursor: string | null;
}

type ErasureStatus = 'pending' | 'approved' | 'rejected' | 'completed' | 'cancelled';

interface ErasureRow {
  id: string;
  resident_id: string;
  reason: string;
  status: ErasureStatus;
  reviewed_at: string | null;
  completed_at: string | null;
  created_at: string;
}

interface ErasureListResp {
  items: ErasureRow[];
  next_cursor: string | null;
}

interface DataExportLogRow {
  id: string;
  resident_id: string;
  generated_at: string;
  format: string;
}

interface DataExportLogListResp {
  items: DataExportLogRow[];
  next_cursor: string | null;
}

interface AuditLogRow {
  id: string;
  action: string;
  entity_type: string;
  entity_id: string | null;
  actor_user_id: string | null;
  actor_role: string;
  at: string;
  before: unknown;
  after: unknown;
}

interface AuditLogListResp {
  items: AuditLogRow[];
  next_cursor: string | null;
}

// ─── T3 (deferred) section types ──────────────────────────────────────

interface VendorJobInvoiceRow {
  id: string;
  invoice_number: string;
  amount_inr: number;
  description: string | null;
  status: string;
  invoice_date: string;
  paid_at: string | null;
  vendor: { id: string; name: string } | null;
  assignment: { complaint_id: string } | null;
}

interface VendorJobComplaintRow {
  id: string;
  title: string;
  category: string;
  status: ComplaintStatus;
  priority: string;
  created_at: string;
  resolved_at: string | null;
  vendor_assignments: Array<{
    id: string;
    resolved_at: string | null;
    sla_due_at: string;
    vendor: { id: string; name: string } | null;
  }>;
}

interface VendorJobsResp {
  complaints: VendorJobComplaintRow[];
  invoices: VendorJobInvoiceRow[];
}

type AdminRoleCode =
  | 'admin'
  | 'accountant'
  | 'treasurer'
  | 'secretary'
  | 'president'
  | 'building_manager';

interface GovernanceRoleResp {
  current_role: AdminRoleCode | null;
  admin_record_id: string | null;
  admin_active: boolean | null;
  history: AuditLogRow[];
  wins: Array<{
    id: string;
    vote_count: number;
    position: number;
    computed_at: string;
    election: {
      id: string;
      title: string;
      role: string;
      term_start: string | null;
      term_end: string | null;
      results_published_at: string | null;
    } | null;
  }>;
}

interface LoginHistoryRow {
  id: string;
  at: string;
  ip: string | null;
  user_agent: string | null;
  actor_role: string;
}

interface LoginHistoryResp {
  items: LoginHistoryRow[];
  next_cursor: string | null;
}

interface SosEventRow {
  id: string;
  at: string;
  payload: unknown;
  ip: string | null;
  actor_role: string;
}

interface SosEventsResp {
  items: SosEventRow[];
}

interface PrivacyResp {
  exports: Array<{ id: string; resident_id: string; generated_at: string; format: string }>;
  erasures: Array<{
    id: string;
    resident_id: string;
    reason: string;
    status: ErasureStatus;
    reviewed_at: string | null;
    completed_at: string | null;
    created_at: string;
  }>;
  audit: AuditLogRow[];
}

interface AdminNoteRow {
  id: string;
  body: string;
  tags: string[];
  author_user_id: string;
  created_at: string;
  updated_at: string;
}

interface AdminNotesResp {
  items: AdminNoteRow[];
}

interface ResidentKycResp {
  id_proof_type: string | null;
  id_proof_last4: string | null;
  has_id_proof: boolean;
  has_aadhaar: boolean;
  has_pan: boolean;
  verified: boolean;
  verified_at: string | null;
  verified_by_user_id: string | null;
  updated_at: string;
}

const ADMIN_ROLE_LABEL: Record<AdminRoleCode, string> = {
  admin: 'Admin',
  accountant: 'Accountant',
  treasurer: 'Treasurer',
  secretary: 'Secretary',
  president: 'President',
  building_manager: 'Building manager'
};

// ─── small helpers ────────────────────────────────────────────────────

function fmtInr(rupees: number): string {
  return `₹${Math.abs(rupees).toLocaleString('en-IN')}`;
}

// Hash-to-color helper for admin-note tags. Stable across renders / sessions.
const TAG_PALETTE = [
  'bg-vt-primary-100 text-vt-primary',
  'bg-vt-secondary-100 text-vt-text',
  'bg-[color:var(--vt-warn-100)] text-[color:var(--vt-warn)]',
  'bg-[color:var(--vt-info-100,#dbeafe)] text-[color:var(--vt-info,#1e40af)]',
  'bg-[color:var(--vt-alert-100,#fee2e2)] text-[color:var(--vt-alert,#991b1b)]',
  'bg-[color:var(--vt-safe-100,#dcfce7)] text-[color:var(--vt-safe,#166534)]'
];
function tagColorClass(tag: string): string {
  let h = 0;
  for (let i = 0; i < tag.length; i++) h = ((h << 5) - h + tag.charCodeAt(i)) | 0;
  const idx = Math.abs(h) % TAG_PALETTE.length;
  return TAG_PALETTE[idx]!;
}

// Truncate UA for table display; full UA available on title hover.
function shortUa(ua: string | null): string {
  if (!ua) return '—';
  if (ua.length <= 48) return ua;
  return ua.slice(0, 45) + '…';
}

/**
 * useResidentBlobUrl — mirrors useGuardBlobUrl (guard-detail.tsx). Fetches
 * an auth-gated KYC blob and exposes it as an object-URL for <img>. Refetches
 * whenever `cacheBust` flips (e.g. has_id_proof toggles or updated_at moves).
 */
function useResidentBlobUrl(
  residentId: string | null | undefined,
  docType: 'id_proof' | 'aadhaar' | 'pan',
  cacheBust: string | boolean | null | undefined
): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!residentId || !cacheBust) {
      setUrl(null);
      return;
    }
    let revoked = false;
    let createdUrl: string | null = null;
    api
      .get<Blob>(`/residents/${residentId}/kyc/${docType}`, { responseType: 'blob' })
      .then((res) => {
        if (revoked) return;
        createdUrl = URL.createObjectURL(res.data);
        setUrl(createdUrl);
      })
      .catch(() => {
        if (!revoked) setUrl(null);
      });
    return () => {
      revoked = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [residentId, docType, cacheBust]);
  return url;
}

function initialsFor(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}

const RELATION_LABEL: Record<Relation, string> = {
  owner: 'Owner',
  tenant: 'Tenant',
  family: 'Family',
  paying_guest: 'Paying guest'
};

const RELATION_VARIANT: Record<Relation, 'info' | 'warn' | 'ok' | 'muted'> = {
  owner: 'info',
  tenant: 'warn',
  family: 'ok',
  paying_guest: 'muted'
};

const VISITOR_STATUS_VARIANT: Record<VisitorStatus, 'ok' | 'warn' | 'alert' | 'muted' | 'info'> = {
  pending: 'warn',
  approved: 'info',
  denied: 'alert',
  checked_in: 'ok',
  checked_out: 'muted',
  expired: 'muted'
};

const COMPLAINT_STATUS_VARIANT: Record<ComplaintStatus, 'ok' | 'warn' | 'alert' | 'muted' | 'info'> = {
  open: 'alert',
  assigned: 'warn',
  in_progress: 'info',
  resolved: 'ok',
  closed: 'muted',
  rejected: 'muted'
};

const APPROVAL_STATUS_VARIANT: Record<ApprovalStatus, 'ok' | 'warn' | 'alert' | 'muted'> = {
  auto_approved: 'ok',
  approved: 'ok',
  pending_resident: 'warn',
  rejected: 'alert'
};

const APPROVAL_STATUS_LABEL: Record<ApprovalStatus, string> = {
  auto_approved: 'Auto-approved',
  approved: 'Approved',
  pending_resident: 'Pending',
  rejected: 'Rejected'
};

function unitLabel(unit: ResidentDetail['unit']): string {
  if (!unit) return '—';
  const block = unit.block?.name;
  return block ? `${block} / ${unit.number}` : unit.number;
}

const VEHICLE_KIND_LABEL: Record<VehicleKind, string> = {
  car: 'Car',
  bike: 'Bike',
  cycle: 'Cycle',
  ev: 'EV',
  commercial: 'Commercial',
  other: 'Other'
};

const VEHICLE_ENTRY_STATUS_VARIANT: Record<VehicleEntryStatus, 'ok' | 'muted'> = {
  inside: 'ok',
  exited: 'muted'
};

const TRANSITION_STATUS_VARIANT: Record<TransitionStatus, 'ok' | 'warn' | 'alert' | 'muted' | 'info'> = {
  draft: 'muted',
  notice_given: 'warn',
  handover_scheduled: 'info',
  key_transferred: 'info',
  noc_issued: 'info',
  completed: 'ok',
  cancelled: 'alert'
};

const TRANSITION_KIND_LABEL: Record<TransitionKind, string> = {
  move_in: 'Move-in',
  move_out: 'Move-out',
  tenant_swap: 'Tenant swap'
};

const BOOKING_STATUS_VARIANT: Record<BookingStatus, 'ok' | 'warn' | 'alert' | 'muted' | 'info'> = {
  pending: 'warn',
  confirmed: 'info',
  cancelled: 'muted',
  completed: 'ok',
  no_show: 'alert'
};

const NOC_PURPOSE_LABEL: Record<NocPurpose, string> = {
  move_out: 'Move-out',
  vehicle_registration: 'Vehicle registration',
  passport: 'Passport',
  electricity_connection: 'Electricity',
  gas_connection: 'Gas',
  internet_connection: 'Internet',
  general: 'General',
  other: 'Other'
};

const ERASURE_STATUS_VARIANT: Record<ErasureStatus, 'ok' | 'warn' | 'alert' | 'muted' | 'info'> = {
  pending: 'warn',
  approved: 'info',
  rejected: 'alert',
  completed: 'ok',
  cancelled: 'muted'
};

const NOTICE_CATEGORY_LABEL: Record<NoticeCategory, string> = {
  general: 'General',
  maintenance: 'Maintenance',
  security: 'Security',
  event: 'Event',
  billing: 'Billing'
};

const HELPER_KIND_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'maid', label: 'Maid' },
  { value: 'cook', label: 'Cook' },
  { value: 'driver', label: 'Driver' },
  { value: 'nanny', label: 'Nanny' },
  { value: 'gardener', label: 'Gardener' },
  { value: 'ironwala', label: 'Ironwala' }
] as const;

type HelperKindFilter = (typeof HELPER_KIND_FILTERS)[number]['value'];

/**
 * Friendly action label for a small set of audit actions we surface on the
 * resident profile. Falls back to the raw action code if we don't have a
 * dedicated copy line — keeps the timeline honest rather than hiding noise.
 */
const AUDIT_ACTION_LABEL: Record<string, string> = {
  'resident.phone_change_request': 'Phone change requested',
  'resident.phone_change_confirm': 'Phone change confirmed',
  'resident.profile_update': 'Profile updated',
  'resident.deleted': 'Resident archived',
  'resident.restored': 'Resident restored'
};

function inrShort(amount: number | null | undefined): string {
  if (amount === null || amount === undefined) return '—';
  return `₹${Math.abs(amount).toLocaleString('en-IN')}`;
}

// ─── section wrappers ─────────────────────────────────────────────────

function SectionTitle({
  icon,
  title,
  subtitle,
  action
}: {
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
}): JSX.Element {
  return (
    <div className="mb-vt-3 flex items-start justify-between gap-vt-3">
      <div className="flex items-start gap-vt-2">
        <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-vt-sm bg-vt-primary-100 text-vt-primary">
          {icon}
        </span>
        <div>
          <h2 className="text-vt-16 font-semibold tracking-[-0.01em] text-vt-text">{title}</h2>
          {subtitle ? <p className="mt-0.5 text-vt-12 text-vt-text-muted">{subtitle}</p> : null}
        </div>
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  );
}

function LabelValue({
  label,
  children
}: {
  label: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-vt-12 uppercase tracking-wide text-vt-text-soft">{label}</span>
      <span className="text-vt-13 text-vt-text">{children}</span>
    </div>
  );
}

// ─── page ─────────────────────────────────────────────────────────────

export function ResidentDetailPage(): JSX.Element {
  const { id } = useParams({ strict: false }) as { id: string };
  const navigate = useNavigate();

  // Helper kind filter state (Maid/Cook/Driver/Nanny/Gardener/Ironwala/All).
  // Lives on the FV section card; defaults to "all".
  const [helperKind, setHelperKind] = useState<HelperKindFilter>('all');
  // Requests card sub-tab. 'all' shows the merged feed.
  const [requestsTab, setRequestsTab] = useState<string>('all');

  // 1. Resident core (with unit + co_residents + family_count)
  const residentQ = useQuery({
    queryKey: ['resident-detail', id],
    queryFn: () => get<ResidentDetail>(`/residents/${id}`)
  });

  const resident = residentQ.data;
  const unitId = resident?.unit_id;

  // 2. Family members (first 10)
  const familyQ = useQuery({
    queryKey: ['resident-detail', 'family', id],
    queryFn: () => get<FamilyListResp>('/family', { params: { resident_id: id, limit: 10 } }),
    enabled: Boolean(id)
  });

  // 3. Recent visitors for this unit (last 5)
  const visitorsQ = useQuery({
    queryKey: ['resident-detail', 'visitors', unitId],
    queryFn: () =>
      get<VisitorListResp>('/visitors', {
        params: { unit_id: unitId, limit: 5, sort: 'created_desc' }
      }),
    enabled: Boolean(unitId)
  });

  // 4. Bills for this unit (cap at 100; client-aggregate)
  const billsQ = useQuery({
    queryKey: ['resident-detail', 'bills', unitId],
    queryFn: () =>
      get<BillListResp>('/bills', {
        params: { unit_id: unitId, limit: 100, sort: 'due_date_desc' }
      }),
    enabled: Boolean(unitId)
  });

  // 5. FV assignments on this unit (up to 20)
  const fvQ = useQuery({
    queryKey: ['resident-detail', 'fv-assignments', unitId],
    queryFn: () =>
      get<FvAssignmentListResp>('/frequent-visitor-assignments', {
        params: { unit_id: unitId, limit: 20 }
      }),
    enabled: Boolean(unitId)
  });

  // 6. Recent complaints for this unit (last 5)
  const complaintsQ = useQuery({
    queryKey: ['resident-detail', 'complaints', unitId],
    queryFn: () =>
      get<ComplaintListResp>('/complaints', {
        params: { unit_id: unitId, limit: 5, sort: 'created_desc' }
      }),
    enabled: Boolean(unitId)
  });

  // ─── T1 + T2 + Requests parallel queries ─────────────────────────────

  // 7. Vehicles registered against this unit (filtered server-side).
  const vehiclesQ = useQuery({
    queryKey: ['resident-detail', 'vehicles', unitId],
    queryFn: () =>
      get<VehicleListResp>('/vehicles', {
        params: { unit_id: unitId, limit: 20 }
      }),
    enabled: Boolean(unitId)
  });

  // 8. Gate vehicle-entries — endpoint does NOT accept unit_id; pull a page
  // and client-filter to entries that match a registered plate on this unit.
  // Limit is the server cap; UI shows the most recent 5 matches.
  const vehicleEntriesQ = useQuery({
    queryKey: ['resident-detail', 'vehicle-entries', unitId],
    queryFn: () =>
      get<VehicleEntryListResp>('/vehicle-entries', {
        params: { limit: 100 }
      }),
    enabled: Boolean(unitId)
  });

  // 9. Tenancy transitions for this unit — server-filtered. Used by both
  // the Tenancy / Lease card and the Move-in/out timeline + Requests card.
  const transitionsQ = useQuery({
    queryKey: ['resident-detail', 'transitions', unitId],
    queryFn: () =>
      get<TransitionListResp>('/tenancy-transitions', {
        params: { unit_id: unitId, limit: 20 }
      }),
    enabled: Boolean(unitId)
  });

  // 10. NOCs filtered by resident_id (server-filtered).
  const nocsQ = useQuery({
    queryKey: ['resident-detail', 'nocs', id],
    queryFn: () =>
      get<NocListResp>('/nocs', {
        params: { resident_id: id, limit: 20 }
      }),
    enabled: Boolean(id)
  });

  // 11. Amenity bookings — filtered by resident_id.
  const bookingsQ = useQuery({
    queryKey: ['resident-detail', 'bookings', id],
    queryFn: () =>
      get<BookingListResp>('/bookings', {
        params: { resident_id: id, limit: 5 }
      }),
    enabled: Boolean(id)
  });

  // 12. Notices — endpoint does NOT accept unit_id; pull recent and
  // client-filter to (audience=all) OR (audience=unit and audience_unit_id
  // matches) OR (audience=block and audience_block_id matches this unit's
  // block). Surface the latest 5 after filter.
  const noticesQ = useQuery({
    queryKey: ['resident-detail', 'notices', unitId],
    queryFn: () =>
      get<NoticeListResp>('/notices', {
        params: { limit: 50, sort: 'created_desc', status: 'published' }
      }),
    enabled: Boolean(unitId)
  });

  // 13. Community posts — endpoint does NOT accept author_id; pull recent
  // and client-filter to author_id = this resident. Surface the latest 5.
  const communityQ = useQuery({
    queryKey: ['resident-detail', 'community', id],
    queryFn: () =>
      get<CommunityListResp>('/community/posts', {
        params: { limit: 50, sort: 'created_desc' }
      }),
    enabled: Boolean(id)
  });

  // 14. DPDP erasure requests — endpoint does NOT accept resident_id; pull
  // a page and client-filter to this resident.
  const erasureQ = useQuery({
    queryKey: ['resident-detail', 'erasure-requests', id],
    queryFn: () =>
      get<ErasureListResp>('/erasure-requests', {
        params: { limit: 50 }
      }),
    enabled: Boolean(id)
  });

  // 15. Data-export log — server-filtered by resident_id.
  const exportsQ = useQuery({
    queryKey: ['resident-detail', 'data-export-log', id],
    queryFn: () =>
      get<DataExportLogListResp>('/dpdp/data-export-log', {
        params: { resident_id: id, limit: 10 }
      }),
    enabled: Boolean(id)
  });

  // 16. Audit log scoped to entity_type=resident + entity_id=this resident —
  // covers phone-change request/confirm + profile updates.
  const auditQ = useQuery({
    queryKey: ['resident-detail', 'audit-log', id],
    queryFn: () =>
      get<AuditLogListResp>('/audit-log', {
        params: { entity_type: 'resident', entity_id: id, limit: 20 }
      }),
    enabled: Boolean(id)
  });

  // ─── T3 queries (vendor jobs / governance / login / SOS / privacy / notes / KYC) ──
  const queryClient = useQueryClient();

  const vendorJobsQ = useQuery({
    queryKey: ['resident-detail', 'vendor-jobs', id],
    queryFn: () => get<VendorJobsResp>(`/residents/${id}/vendor-jobs`, { params: { limit: 20 } }),
    enabled: Boolean(id)
  });

  const govRoleQ = useQuery({
    queryKey: ['resident-detail', 'governance-role', id],
    queryFn: () => get<GovernanceRoleResp>(`/residents/${id}/governance-role`),
    enabled: Boolean(id)
  });

  const loginHistoryQ = useQuery({
    queryKey: ['resident-detail', 'login-history', id],
    queryFn: () => get<LoginHistoryResp>(`/residents/${id}/login-history`, { params: { limit: 20 } }),
    enabled: Boolean(id)
  });

  const sosEventsQ = useQuery({
    queryKey: ['resident-detail', 'sos-events', id],
    queryFn: () => get<SosEventsResp>(`/residents/${id}/sos-events`, { params: { limit: 20 } }),
    enabled: Boolean(id)
  });

  const privacyQ = useQuery({
    queryKey: ['resident-detail', 'privacy', id],
    queryFn: () => get<PrivacyResp>(`/residents/${id}/privacy`),
    enabled: Boolean(id)
  });

  const adminNotesQ = useQuery({
    queryKey: ['resident-detail', 'admin-notes', id],
    queryFn: () => get<AdminNotesResp>(`/residents/${id}/admin-notes`),
    enabled: Boolean(id)
  });

  const kycQ = useQuery({
    queryKey: ['resident-detail', 'kyc', id],
    queryFn: () => get<ResidentKycResp>(`/residents/${id}/kyc`),
    enabled: Boolean(id)
  });

  // Note CRUD mutations
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [editingNote, setEditingNote] = useState<AdminNoteRow | null>(null);
  const [noteDraftBody, setNoteDraftBody] = useState('');
  const [noteDraftTags, setNoteDraftTags] = useState('');
  const [pendingDeleteNoteId, setPendingDeleteNoteId] = useState<string | null>(null);

  const createNoteMutation = useMutation({
    mutationFn: (payload: { body: string; tags: string[] }) =>
      post<AdminNoteRow>(`/residents/${id}/admin-notes`, payload),
    onSuccess: () => {
      toast.success('Note added');
      void queryClient.invalidateQueries({ queryKey: ['resident-detail', 'admin-notes', id] });
      setNoteDialogOpen(false);
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const updateNoteMutation = useMutation({
    mutationFn: ({ noteId, ...payload }: { noteId: string; body?: string; tags?: string[] }) =>
      patch<AdminNoteRow>(`/residents/admin-notes/${noteId}`, payload),
    onSuccess: () => {
      toast.success('Note updated');
      void queryClient.invalidateQueries({ queryKey: ['resident-detail', 'admin-notes', id] });
      setNoteDialogOpen(false);
      setEditingNote(null);
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const deleteNoteMutation = useMutation({
    mutationFn: (noteId: string) => del(`/residents/admin-notes/${noteId}`),
    onSuccess: () => {
      toast.success('Note deleted');
      void queryClient.invalidateQueries({ queryKey: ['resident-detail', 'admin-notes', id] });
      setPendingDeleteNoteId(null);
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  function openAddNote(): void {
    setEditingNote(null);
    setNoteDraftBody('');
    setNoteDraftTags('');
    setNoteDialogOpen(true);
  }
  function openEditNote(n: AdminNoteRow): void {
    setEditingNote(n);
    setNoteDraftBody(n.body);
    setNoteDraftTags(n.tags.join(', '));
    setNoteDialogOpen(true);
  }
  function submitNote(): void {
    const body = noteDraftBody.trim();
    if (!body) {
      toast.error('Note body is required');
      return;
    }
    const tags = noteDraftTags
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean)
      .slice(0, 20);
    if (editingNote) {
      updateNoteMutation.mutate({ noteId: editingNote.id, body, tags });
    } else {
      createNoteMutation.mutate({ body, tags });
    }
  }

  // KYC mutations
  const [uploadingKyc, setUploadingKyc] = useState<null | 'id_proof' | 'aadhaar' | 'pan'>(null);
  const uploadKycMutation = useMutation({
    mutationFn: async ({
      docType,
      file
    }: {
      docType: 'id_proof' | 'aadhaar' | 'pan';
      file: File;
    }) => {
      const fd = new FormData();
      fd.append('file', file);
      await api.post(`/residents/${id}/kyc/${docType}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    },
    onMutate: ({ docType }) => setUploadingKyc(docType),
    onSuccess: (_d, { docType }) => {
      toast.success(`${docType.replace('_', ' ')} uploaded`);
      void queryClient.invalidateQueries({ queryKey: ['resident-detail', 'kyc', id] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
    onSettled: () => setUploadingKyc(null)
  });

  const verifyKycMutation = useMutation({
    mutationFn: () => post(`/residents/${id}/kyc/verify`),
    onSuccess: () => {
      toast.success('KYC verified');
      void queryClient.invalidateQueries({ queryKey: ['resident-detail', 'kyc', id] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  // KYC blob URLs — refetch when has_<doc> + updated_at change.
  const kycCacheBust = kycQ.data ? kycQ.data.updated_at : null;
  const idProofUrl = useResidentBlobUrl(
    id,
    'id_proof',
    kycQ.data?.has_id_proof ? kycCacheBust : null
  );
  const aadhaarUrl = useResidentBlobUrl(
    id,
    'aadhaar',
    kycQ.data?.has_aadhaar ? kycCacheBust : null
  );
  const panUrl = useResidentBlobUrl(id, 'pan', kycQ.data?.has_pan ? kycCacheBust : null);

  const billStats = useMemo(() => {
    const items = billsQ.data?.items ?? [];
    let totalBilled = 0;
    let totalPaid = 0;
    let outstanding = 0;
    let lastPaymentAt: string | null = null;
    for (const b of items) {
      const lateFee = b.late_fee_inr_computed ?? b.late_fee_inr ?? 0;
      const total = b.total_due_inr ?? b.amount_inr + lateFee;
      totalBilled += total;
      if (b.status === 'paid') {
        totalPaid += total;
        if (b.paid_at && (!lastPaymentAt || b.paid_at > lastPaymentAt)) {
          lastPaymentAt = b.paid_at;
        }
      } else if (b.status === 'unpaid' || b.status === 'overdue') {
        outstanding += total;
      }
    }
    return { totalBilled, totalPaid, outstanding, lastPaymentAt, count: items.length };
  }, [billsQ.data]);

  // Pendings card — sharper view of "what's owed" surfaced separately from
  // the headline Accounts KPI strip. Computes: outstanding total, overdue
  // count, oldest unpaid due date, late fees accrued so far.
  const pendings = useMemo(() => {
    const items = billsQ.data?.items ?? [];
    let outstanding = 0;
    let lateFeeAccrued = 0;
    let overdueCount = 0;
    let oldestUnpaidDue: string | null = null;
    for (const b of items) {
      if (b.status === 'unpaid' || b.status === 'overdue') {
        const lateFee = b.late_fee_inr_computed ?? b.late_fee_inr ?? 0;
        const total = b.total_due_inr ?? b.amount_inr + lateFee;
        outstanding += total;
        lateFeeAccrued += lateFee;
        if (b.status === 'overdue') overdueCount += 1;
        if (!oldestUnpaidDue || b.due_date < oldestUnpaidDue) {
          oldestUnpaidDue = b.due_date;
        }
      }
    }
    return { outstanding, lateFeeAccrued, overdueCount, oldestUnpaidDue };
  }, [billsQ.data]);

  // Recent 5 bills — sorted by due_date descending (server already does this
  // for the bills list; we just slice). Renders as a small table below the
  // Accounts KPI strip.
  const recentBills = useMemo(() => {
    const items = billsQ.data?.items ?? [];
    return items.slice(0, 5);
  }, [billsQ.data]);

  // Tenancy transitions involving this resident (incoming OR outgoing).
  // Used by the Tenancy / Lease card and the Move-in/out timeline.
  const involvedTransitions = useMemo(() => {
    const all = transitionsQ.data?.items ?? [];
    return all.filter(
      (t) => t.outgoing_resident_id === id || t.incoming_resident_id === id
    );
  }, [transitionsQ.data, id]);

  // Active lease — most-recent non-cancelled transition that has lease
  // financials filled in. Surfaces only when the resident is a tenant OR
  // there's an active lease transition referencing them.
  const activeLease = useMemo(() => {
    if (!involvedTransitions.length) return null;
    const candidates = involvedTransitions.filter(
      (t) =>
        t.status !== 'cancelled' &&
        (t.monthly_rent_inr !== null ||
          t.deposit_inr !== null ||
          t.lease_end_date !== null)
    );
    return candidates[0] ?? null;
  }, [involvedTransitions]);

  // Helpers / FV — apply kind filter client-side (FV catalog codes match the
  // chip values for our seeded labels; unknown codes fall through "All").
  const filteredFvAssignments = useMemo(() => {
    const all = fvQ.data?.items ?? [];
    if (helperKind === 'all') return all;
    return all.filter(
      (a) => a.frequent_visitor.kind?.toLowerCase() === helperKind
    );
  }, [fvQ.data, helperKind]);

  // Vehicle entries scoped to this unit. The /v1/vehicle-entries endpoint
  // doesn't accept unit_id, so we client-filter against the registered
  // vehicles' plates for this unit. Caveat: walk-in / visitor entries on
  // unregistered plates linked to this unit at gate time won't surface
  // (their unit_id is set on the entry row but we don't read that field
  // here without a server-side filter — code TODO once API gains support).
  const recentVehicleEntries = useMemo(() => {
    const vehicles = vehiclesQ.data?.items ?? [];
    const platesOnUnit = new Set(vehicles.map((v) => v.number_plate));
    const entries = vehicleEntriesQ.data?.items ?? [];
    return entries.filter((e) => platesOnUnit.has(e.number_plate)).slice(0, 5);
  }, [vehiclesQ.data, vehicleEntriesQ.data]);

  // Notices delivered to this unit — endpoint doesn't accept unit_id, so we
  // client-filter the recent page on the three audience flavours we ship:
  //   - audience='all'    : society-wide; everyone sees it
  //   - audience='unit'   : only the specific audience_unit_id
  //   - audience='block'  : only that audience_block_id (we surface notices
  //                         where audience_block_id == this unit's block)
  // 'owners'/'tenants' audience checks would also belong here once that
  // filter ships in the API; for now they're treated as society-wide.
  const blockId = resident?.unit?.block?.id ?? null;
  const unitNoticeRows = useMemo(() => {
    const all = noticesQ.data?.items ?? [];
    return all
      .filter((n) => {
        if (n.audience === 'all' || n.audience === 'owners' || n.audience === 'tenants') return true;
        if (n.audience === 'unit') return n.audience_unit_id === unitId;
        if (n.audience === 'block') return n.audience_block_id === blockId;
        return false;
      })
      .slice(0, 5);
  }, [noticesQ.data, unitId, blockId]);

  // Community posts authored by this resident — endpoint doesn't accept
  // author_id; client-filter the first 50 visible+hidden posts.
  const myCommunityPosts = useMemo(() => {
    const all = communityQ.data?.items ?? [];
    return all
      .filter((p) => p.author_role === 'resident' && p.author_id === id)
      .slice(0, 5);
  }, [communityQ.data, id]);

  // Pre-approvals issued by this resident — re-uses recent /visitors page
  // (already scoped to unit) and client-filters by preapproved_by_resident_id.
  // The Visitor list response doesn't currently expose preapproved_by_*,
  // so we read it via the casted row shape. If it's not present we treat
  // a status='approved' (preapproved-like) visitor on this unit as a match —
  // good enough for the Requests roll-up; full attribution lands when the
  // API surfaces preapproved_by_resident_id in the list payload.
  const preapprovals = useMemo(() => {
    const items = (visitorsQ.data?.items ?? []) as Array<
      VisitorRow & { preapproved_by_resident_id?: string | null }
    >;
    return items
      .filter((v) =>
        v.preapproved_by_resident_id
          ? v.preapproved_by_resident_id === id
          : v.status === 'approved'
      )
      .slice(0, 5);
  }, [visitorsQ.data, id]);

  // Erasure requests filed by this resident (client-filtered).
  const myErasures = useMemo(() => {
    const all = erasureQ.data?.items ?? [];
    return all.filter((e) => e.resident_id === id).slice(0, 5);
  }, [erasureQ.data, id]);

  // Audit phone-change events filtered out for the Requests card.
  const phoneChangeEvents = useMemo(() => {
    const all = auditQ.data?.items ?? [];
    return all
      .filter(
        (r) =>
          r.action === 'resident.phone_change_request' ||
          r.action === 'resident.phone_change_confirm'
      )
      .slice(0, 5);
  }, [auditQ.data]);

  // Pending FV assignment approvals for this resident's unit (resident must
  // still approve). Reuses the existing fvQ data.
  const pendingFvApprovals = useMemo(() => {
    const all = fvQ.data?.items ?? [];
    return all.filter((a) => a.approval_status === 'pending_resident');
  }, [fvQ.data]);

  // Requests roll-up counts — for the TabsPills count badges.
  const requestCounts = useMemo(() => {
    return {
      fv_approvals: pendingFvApprovals.length,
      nocs: nocsQ.data?.items.length ?? 0,
      transitions: involvedTransitions.length,
      phone_change: phoneChangeEvents.length,
      dpdp: myErasures.length + (exportsQ.data?.items.length ?? 0),
      preapprovals: preapprovals.length
    };
  }, [
    pendingFvApprovals.length,
    nocsQ.data,
    involvedTransitions.length,
    phoneChangeEvents.length,
    myErasures.length,
    exportsQ.data,
    preapprovals.length
  ]);

  // ─── loading / error states ─────────────────────────────────────────

  if (residentQ.isLoading) {
    return (
      <>
        <PageHeader title="Resident profile" description="Loading resident details…" />
        <div className="mx-auto w-full max-w-[1200px] space-y-vt-4 px-vt-6 pb-vt-8 pt-vt-4">
          <Card>
            <CardContent className="p-vt-5">
              <Skeleton className="h-24 w-full" />
            </CardContent>
          </Card>
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i}>
              <CardContent className="p-vt-5">
                <Skeleton className="h-32 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      </>
    );
  }

  if (residentQ.isError || !resident) {
    return (
      <>
        <PageHeader title="Resident profile" description="Could not load this resident." />
        <div className="mx-auto w-full max-w-[1200px] px-vt-6 pb-vt-8 pt-vt-4">
          <Card>
            <CardContent className="p-vt-5">
              <EmptyState
                icon={<AlertTriangle className="h-6 w-6" />}
                title="Resident not found"
                description={
                  residentQ.error
                    ? getErrorMessage(residentQ.error)
                    : 'This resident may have been removed or you may not have access.'
                }
                action={
                  <Button variant="secondary" onClick={() => void navigate({ to: '/residents' })}>
                    <ArrowLeft className="h-4 w-4" /> Back to residents
                  </Button>
                }
              />
            </CardContent>
          </Card>
        </div>
      </>
    );
  }

  // ─── header card content ───────────────────────────────────────────

  const isDeleted = Boolean(resident.deleted_at);
  const livesInUnit = resident.lives_in_unit !== false;

  const headerActions = (
    <div className="flex flex-wrap items-center gap-vt-2">
      <Button
        variant="secondary"
        size="sm"
        asChild
        title="Edit profile (opens inline edit dialog)"
      >
        <Link to="/residents" search={{ edit: resident.id } as never}>
          <Pencil className="h-4 w-4" /> Edit
        </Link>
      </Button>
      <Button variant="secondary" size="sm" asChild title="Open full ledger">
        <Link to="/residents/$id/ledger" params={{ id: resident.id }}>
          <Receipt className="h-4 w-4" /> Ledger
        </Link>
      </Button>
      <Button variant="secondary" size="sm" asChild title="Manage family members">
        <Link to="/family" search={{ resident_id: resident.id } as never}>
          <Users className="h-4 w-4" /> Family
        </Link>
      </Button>
    </div>
  );

  return (
    <>
      <PageHeader
        title={resident.full_name}
        description={`${RELATION_LABEL[resident.relation]} · ${unitLabel(resident.unit)} · ${resident.phone}`}
        actions={
          <Button variant="ghost" size="sm" onClick={() => navigate({ to: '/residents' })} title="Back to residents list">
            <ArrowLeft className="h-4 w-4" /> Back
          </Button>
        }
      />

      <div className="mx-auto w-full max-w-[1200px] space-y-vt-4 px-vt-6 pb-vt-8 pt-vt-4">
        {/* ── Header card ─────────────────────────────────────────── */}
        <Card>
          <CardContent className="flex flex-col gap-vt-4 p-vt-5 md:flex-row md:items-start md:justify-between">
            <div className="flex items-start gap-vt-4">
              {/*
                Passport-style avatar: 140×170 (≈3.5:4.5, classic Indian passport
                ratio), rounded-vt-md, vt-border. Residents don't have a photo
                column today, so we render initials — image swap-in will replace
                this when ResidentPhoto column lands (flagged as schema TODO).
              */}
              <span
                className="flex shrink-0 items-center justify-center rounded-vt-md border border-vt-border bg-vt-primary-100 text-vt-32 font-semibold text-vt-primary"
                style={{ width: 140, height: 170 }}
                aria-hidden="true"
              >
                {initialsFor(resident.full_name)}
              </span>
              <div className="min-w-0">
                <h2 className="truncate text-vt-24 font-semibold tracking-[-0.01em] text-vt-text">
                  {resident.full_name}
                </h2>
                <div className="mt-vt-1 flex flex-wrap items-center gap-vt-3 text-vt-13 text-vt-text-muted">
                  <span className="inline-flex items-center gap-1">
                    <Phone className="h-3.5 w-3.5" strokeWidth={1.75} /> {resident.phone}
                  </span>
                  {resident.email ? (
                    <span className="inline-flex items-center gap-1 truncate">
                      <Mail className="h-3.5 w-3.5" strokeWidth={1.75} /> {resident.email}
                    </span>
                  ) : null}
                  <span className="inline-flex items-center gap-1">
                    <Home className="h-3.5 w-3.5" strokeWidth={1.75} /> {unitLabel(resident.unit)}
                  </span>
                </div>
                <div className="mt-vt-2 flex flex-wrap items-center gap-vt-2">
                  <Badge variant={RELATION_VARIANT[resident.relation]}>
                    {RELATION_LABEL[resident.relation]}
                  </Badge>
                  {resident.is_primary ? <Badge variant="ok">Primary contact</Badge> : null}
                  {livesInUnit ? (
                    <Badge variant="muted">Onsite</Badge>
                  ) : (
                    <Badge variant="warn">Offsite</Badge>
                  )}
                  {isDeleted ? <Badge variant="alert">Deleted</Badge> : <Badge variant="info">Active</Badge>}
                  {resident.move_in_date ? (
                    <Badge variant="muted">
                      <CalendarDays className="h-3 w-3" strokeWidth={1.75} />
                      Moved in {formatDate(resident.move_in_date)}
                    </Badge>
                  ) : null}
                </div>
              </div>
            </div>
            {headerActions}
          </CardContent>
        </Card>

        {/* ── Section 1 · Personal & contact ─────────────────────── */}
        <Card>
          <CardContent className="p-vt-5">
            <SectionTitle
              icon={<UserCircle2 className="h-4 w-4" strokeWidth={1.75} />}
              title="Personal & contact"
              subtitle="Society-held contact + onboarding details"
            />
            <div className="grid grid-cols-1 gap-vt-4 sm:grid-cols-2 md:grid-cols-3">
              <LabelValue label="Full name">{resident.full_name}</LabelValue>
              <LabelValue label="Phone">{resident.phone}</LabelValue>
              <LabelValue label="Email">{resident.email ?? '—'}</LabelValue>
              <LabelValue label="Relation">{RELATION_LABEL[resident.relation]}</LabelValue>
              <LabelValue label="Unit">{unitLabel(resident.unit)}</LabelValue>
              <LabelValue label="Primary contact">{resident.is_primary ? 'Yes' : 'No'}</LabelValue>
              <LabelValue label="Lives in unit">{livesInUnit ? 'Yes (Onsite)' : 'No (Offsite)'}</LabelValue>
              <LabelValue label="Move-in date">
                {resident.move_in_date ? formatDate(resident.move_in_date) : '—'}
              </LabelValue>
              <LabelValue label="Move-out date">
                {resident.move_out_date ? formatDate(resident.move_out_date) : '—'}
              </LabelValue>
              <LabelValue label="Created">{formatDateTime(resident.created_at)}</LabelValue>
              <LabelValue label="Updated">{formatDateTime(resident.updated_at)}</LabelValue>
              {isDeleted ? (
                <LabelValue label="Deleted at">{formatDateTime(resident.deleted_at)}</LabelValue>
              ) : null}
            </div>
          </CardContent>
        </Card>

        {/* ── Section 2 · Family members ─────────────────────────── */}
        <Card>
          <CardContent className="p-vt-5">
            <SectionTitle
              icon={<Users className="h-4 w-4" strokeWidth={1.75} />}
              title="Family members"
              subtitle={
                familyQ.data
                  ? `${resident.family_count} on file`
                  : 'Family rows linked to this resident'
              }
              action={
                <Button variant="link" size="sm" asChild>
                  <Link to="/family" search={{ resident_id: resident.id } as never}>
                    Manage family
                  </Link>
                </Button>
              }
            />
            {familyQ.isLoading ? (
              <Skeleton className="h-20 w-full" />
            ) : familyQ.isError ? (
              <p className="text-vt-13 text-vt-alert">{getErrorMessage(familyQ.error)}</p>
            ) : (familyQ.data?.items.length ?? 0) === 0 ? (
              <p className="text-vt-13 text-vt-text-muted">No family members on file.</p>
            ) : (
              <div className="flex flex-col divide-y divide-vt-border">
                {familyQ.data!.items.map((f) => (
                  <div key={f.id} className="flex items-center gap-vt-3 py-vt-2">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-vt-secondary-100 text-vt-12 font-semibold text-vt-text">
                      {initialsFor(f.full_name)}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-vt-13 font-medium text-vt-text">{f.full_name}</div>
                      <div className="truncate text-vt-12 text-vt-text-muted">
                        {f.relation}
                        {f.phone ? ` · ${f.phone}` : ''}
                        {f.date_of_birth ? ` · DOB ${formatDate(f.date_of_birth)}` : ''}
                      </div>
                    </div>
                  </div>
                ))}
                {resident.family_count > (familyQ.data?.items.length ?? 0) ? (
                  <Link
                    to="/family"
                    search={{ resident_id: resident.id } as never}
                    className="py-vt-2 text-vt-13 font-medium text-vt-primary hover:underline"
                  >
                    +{resident.family_count - (familyQ.data?.items.length ?? 0)} more —
                    open family list →
                  </Link>
                ) : null}
              </div>
            )}
          </CardContent>
        </Card>

        {/* ── Section 3 · Co-residents in the same flat ──────────── */}
        <Card>
          <CardContent className="p-vt-5">
            <SectionTitle
              icon={<UserSquare2 className="h-4 w-4" strokeWidth={1.75} />}
              title="Co-residents on the same flat"
              subtitle={
                resident.co_residents.length === 0
                  ? 'No other residents on this unit'
                  : `${resident.co_residents.length} other active resident${resident.co_residents.length === 1 ? '' : 's'} on ${unitLabel(resident.unit)}`
              }
            />
            {resident.co_residents.length === 0 ? (
              <p className="text-vt-13 text-vt-text-muted">
                No other residents on file for this flat.
              </p>
            ) : (
              <div className="flex flex-col divide-y divide-vt-border">
                {resident.co_residents.map((co) => (
                  <Link
                    key={co.id}
                    to="/residents/$id"
                    params={{ id: co.id }}
                    className="flex items-center gap-vt-3 py-vt-2 rounded-vt-sm hover:bg-vt-surface-2/60"
                  >
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-vt-primary-100 text-vt-12 font-semibold text-vt-primary">
                      {initialsFor(co.full_name)}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-vt-13 font-medium text-vt-text">
                        {co.full_name}
                      </div>
                      <div className="truncate text-vt-12 text-vt-text-muted">{co.phone}</div>
                    </div>
                    <div className="flex flex-wrap items-center justify-end gap-1">
                      <Badge variant={RELATION_VARIANT[co.relation]}>
                        {RELATION_LABEL[co.relation]}
                      </Badge>
                      {co.is_primary ? <Badge variant="ok">Primary</Badge> : null}
                      {co.lives_in_unit ? (
                        <Badge variant="muted">Onsite</Badge>
                      ) : (
                        <Badge variant="warn">Offsite</Badge>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* ── Section 4 · Accounts ───────────────────────────────── */}
        {/* Previously labelled "Bills & ledger" — renamed to "Accounts" to
            match the founder spec. KPI strip is unchanged; a new recent-bills
            mini-table sits below it, and a separate Pendings card follows. */}
        <Card>
          <CardContent className="p-vt-5">
            <SectionTitle
              icon={<Wallet className="h-4 w-4" strokeWidth={1.75} />}
              title="Accounts"
              subtitle={
                billsQ.data
                  ? `Aggregated across ${billStats.count} bill${billStats.count === 1 ? '' : 's'} on this flat`
                  : 'Maintenance bill totals for this unit'
              }
              action={
                <Button variant="link" size="sm" asChild>
                  <Link to="/residents/$id/ledger" params={{ id: resident.id }}>
                    Open full ledger →
                  </Link>
                </Button>
              }
            />
            {billsQ.isLoading ? (
              <Skeleton className="h-24 w-full" />
            ) : billsQ.isError ? (
              <p className="text-vt-13 text-vt-alert">
                {getErrorMessage(billsQ.error)} —{' '}
                <button
                  type="button"
                  onClick={() => void billsQ.refetch()}
                  className="font-medium text-vt-primary hover:underline"
                >
                  retry
                </button>
              </p>
            ) : (
              <div className="grid grid-cols-2 gap-vt-3 md:grid-cols-4">
                <KpiTile label="Total billed" value={fmtInr(billStats.totalBilled)} variant="muted" />
                <KpiTile label="Total paid" value={fmtInr(billStats.totalPaid)} variant="ok" />
                <KpiTile
                  label="Outstanding"
                  value={fmtInr(billStats.outstanding)}
                  variant={billStats.outstanding > 0 ? 'alert' : 'muted'}
                />
                <KpiTile
                  label="Last payment"
                  value={billStats.lastPaymentAt ? formatDate(billStats.lastPaymentAt) : '—'}
                  variant="muted"
                />
              </div>
            )}
          </CardContent>
        </Card>

        {/* ── Section 4b · Recent bills (mini-table under Accounts) ── */}
        <Card>
          <CardContent className="p-vt-5">
            <SectionTitle
              icon={<Receipt className="h-4 w-4" strokeWidth={1.75} />}
              title="Recent bills"
              subtitle="Latest 5 bills on this flat with current status"
              action={
                <Button variant="link" size="sm" asChild>
                  <Link to="/bills" search={{ unit_id: unitId } as never}>
                    See all →
                  </Link>
                </Button>
              }
            />
            {billsQ.isLoading ? (
              <Skeleton className="h-24 w-full" />
            ) : billsQ.isError ? (
              <p className="text-vt-13 text-vt-alert">{getErrorMessage(billsQ.error)}</p>
            ) : recentBills.length === 0 ? (
              <p className="text-vt-13 text-vt-text-muted">No bills generated for this flat yet.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Period</TableHead>
                    <TableHead>Due</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead className="text-right">Late fee</TableHead>
                    <TableHead className="text-right">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recentBills.map((b) => {
                    const lateFee = b.late_fee_inr_computed ?? b.late_fee_inr ?? 0;
                    return (
                      <TableRow key={b.id}>
                        <TableCell className="text-vt-13 text-vt-text">{b.period_label}</TableCell>
                        <TableCell className="text-vt-13 text-vt-text-muted">
                          {formatDate(b.due_date)}
                        </TableCell>
                        <TableCell className="text-right text-vt-13 text-vt-text">
                          {fmtInr(b.amount_inr)}
                        </TableCell>
                        <TableCell className="text-right text-vt-13 text-vt-text-muted">
                          {lateFee > 0 ? fmtInr(lateFee) : '—'}
                        </TableCell>
                        <TableCell className="text-right">
                          <Badge
                            variant={
                              b.status === 'paid'
                                ? 'ok'
                                : b.status === 'overdue'
                                  ? 'alert'
                                  : b.status === 'waived'
                                    ? 'muted'
                                    : 'warn'
                            }
                            className="capitalize"
                          >
                            {b.status}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* ── Section 4c · Pendings (emphasised) ────────────────── */}
        {/* Re-surfaces the "what does this resident owe" view as its own
            card so it can't be missed in a glance. Computed from the same
            bills query — no extra API call. */}
        <Card>
          <CardContent className="p-vt-5">
            <SectionTitle
              icon={<Coins className="h-4 w-4" strokeWidth={1.75} />}
              title="Pendings"
              subtitle="Outstanding dues + overdue snapshot for this flat"
              action={
                <Button variant="link" size="sm" asChild>
                  <Link to="/residents/$id/ledger" params={{ id: resident.id }}>
                    Open ledger →
                  </Link>
                </Button>
              }
            />
            {billsQ.isLoading ? (
              <Skeleton className="h-24 w-full" />
            ) : billsQ.isError ? (
              <p className="text-vt-13 text-vt-alert">{getErrorMessage(billsQ.error)}</p>
            ) : (
              <div className="grid grid-cols-2 gap-vt-3 md:grid-cols-4">
                <KpiTile
                  label="Outstanding"
                  value={fmtInr(pendings.outstanding)}
                  variant={pendings.outstanding > 0 ? 'alert' : 'ok'}
                />
                <KpiTile
                  label="Overdue bills"
                  value={String(pendings.overdueCount)}
                  variant={pendings.overdueCount > 0 ? 'alert' : 'muted'}
                />
                <KpiTile
                  label="Oldest unpaid"
                  value={
                    pendings.oldestUnpaidDue
                      ? formatDate(pendings.oldestUnpaidDue)
                      : '—'
                  }
                  variant={pendings.oldestUnpaidDue ? 'alert' : 'muted'}
                />
                <KpiTile
                  label="Late fee accrued"
                  value={fmtInr(pendings.lateFeeAccrued)}
                  variant={pendings.lateFeeAccrued > 0 ? 'alert' : 'muted'}
                />
              </div>
            )}
          </CardContent>
        </Card>

        {/* ── Section 5 · Recent visitors ────────────────────────── */}
        <Card>
          <CardContent className="p-vt-5">
            <SectionTitle
              icon={<Ticket className="h-4 w-4" strokeWidth={1.75} />}
              title="Recent visitors"
              subtitle="Last 5 visitors recorded for this flat"
              action={
                <Button variant="link" size="sm" asChild>
                  <Link to="/visitor-logs" search={{ unit_id: unitId } as never}>
                    See all →
                  </Link>
                </Button>
              }
            />
            {visitorsQ.isLoading ? (
              <Skeleton className="h-24 w-full" />
            ) : visitorsQ.isError ? (
              <p className="text-vt-13 text-vt-alert">{getErrorMessage(visitorsQ.error)}</p>
            ) : (visitorsQ.data?.items.length ?? 0) === 0 ? (
              <p className="text-vt-13 text-vt-text-muted">No visitors logged for this flat yet.</p>
            ) : (
              <div className="flex flex-col divide-y divide-vt-border">
                {visitorsQ.data!.items.map((v) => (
                  <div key={v.id} className="flex items-center gap-vt-3 py-vt-2">
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-vt-13 font-medium text-vt-text">
                        {v.visitor_name}
                      </div>
                      <div className="truncate text-vt-12 text-vt-text-muted">
                        {v.visit_kind} · {formatDateTime(v.created_at)}
                      </div>
                    </div>
                    <Badge variant={VISITOR_STATUS_VARIANT[v.status]} className="capitalize">
                      {v.status.replace('_', ' ')}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* ── Section 6 · Helpers (FV) with kind filter ──────────── */}
        {/* The kind filter chip group lets ops narrow the helper list when
            a flat has many assignments (e.g. only the Cook). Filter is
            client-side over the already-fetched fvQ rows — no extra fetch. */}
        <Card>
          <CardContent className="p-vt-5">
            <SectionTitle
              icon={<ShieldAlert className="h-4 w-4" strokeWidth={1.75} />}
              title="Helpers (frequent visitors)"
              subtitle="Maids, cooks, drivers cleared for routine entry to this unit"
              action={
                <Button variant="link" size="sm" asChild>
                  <Link to="/frequent-visitors">See all →</Link>
                </Button>
              }
            />
            <div className="mb-vt-3">
              <TabsPills
                size="sm"
                value={helperKind}
                onChange={(v) => setHelperKind(v as HelperKindFilter)}
                options={HELPER_KIND_FILTERS.map((k) => ({ value: k.value, label: k.label }))}
                aria-label="Filter helpers by kind"
              />
            </div>
            {fvQ.isLoading ? (
              <Skeleton className="h-24 w-full" />
            ) : fvQ.isError ? (
              <p className="text-vt-13 text-vt-alert">
                {getErrorMessage(fvQ.error)} —{' '}
                <button
                  type="button"
                  onClick={() => void fvQ.refetch()}
                  className="font-medium text-vt-primary hover:underline"
                >
                  retry
                </button>
              </p>
            ) : filteredFvAssignments.length === 0 ? (
              <p className="text-vt-13 text-vt-text-muted">
                {helperKind === 'all'
                  ? 'No frequent visitors assigned to this flat yet.'
                  : `No ${HELPER_KIND_FILTERS.find((k) => k.value === helperKind)?.label.toLowerCase() ?? helperKind} assigned to this flat.`}
              </p>
            ) : (
              <div className="flex flex-col divide-y divide-vt-border">
                {filteredFvAssignments.map((a) => (
                  <div key={a.id} className="flex items-center gap-vt-3 py-vt-2">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-vt-secondary-100 text-vt-12 font-semibold text-vt-text">
                      {initialsFor(a.frequent_visitor.full_name)}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-vt-13 font-medium text-vt-text">
                        {a.frequent_visitor.full_name}
                      </div>
                      <div className="truncate text-vt-12 text-vt-text-muted">
                        <span className="capitalize">{a.frequent_visitor.kind.replace(/_/g, ' ')}</span>
                        {a.days_of_week ? ` · ${a.days_of_week}` : ''}
                        {a.time_start && a.time_end ? ` · ${a.time_start}–${a.time_end}` : ''}
                      </div>
                    </div>
                    <Badge variant={APPROVAL_STATUS_VARIANT[a.approval_status]}>
                      {APPROVAL_STATUS_LABEL[a.approval_status]}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* ── Section · Vehicles ─────────────────────────────────── */}
        <Card>
          <CardContent className="p-vt-5">
            <SectionTitle
              icon={<Car className="h-4 w-4" strokeWidth={1.75} />}
              title="Vehicles"
              subtitle="Registered against this flat + recent gate events"
              action={
                <Button variant="link" size="sm" asChild>
                  <Link to="/vehicles" search={{ unit_id: unitId } as never}>
                    See all →
                  </Link>
                </Button>
              }
            />
            {vehiclesQ.isLoading ? (
              <Skeleton className="h-24 w-full" />
            ) : vehiclesQ.isError ? (
              <p className="text-vt-13 text-vt-alert">
                {getErrorMessage(vehiclesQ.error)} —{' '}
                <button
                  type="button"
                  onClick={() => void vehiclesQ.refetch()}
                  className="font-medium text-vt-primary hover:underline"
                >
                  retry
                </button>
              </p>
            ) : (vehiclesQ.data?.items.length ?? 0) === 0 ? (
              <p className="text-vt-13 text-vt-text-muted">
                No vehicles registered against this flat yet.
              </p>
            ) : (
              <div className="flex flex-col divide-y divide-vt-border">
                {vehiclesQ.data!.items.map((v) => (
                  <div key={v.id} className="flex items-center gap-vt-3 py-vt-2">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-vt-sm bg-vt-secondary-100 text-vt-12 text-vt-text">
                      <Car className="h-4 w-4" strokeWidth={1.75} />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-vt-13 font-medium text-vt-text">
                        {v.number_plate}
                      </div>
                      <div className="truncate text-vt-12 text-vt-text-muted">
                        {VEHICLE_KIND_LABEL[v.kind]}
                        {v.make_model ? ` · ${v.make_model}` : ''}
                        {v.color ? ` · ${v.color}` : ''}
                        {v.parking_slot ? ` · Slot ${v.parking_slot}` : ''}
                      </div>
                    </div>
                    {v.is_active ? (
                      <Badge variant="ok">Active</Badge>
                    ) : (
                      <Badge variant="muted">Inactive</Badge>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Recent gate events for registered vehicles on this flat.
                Client-filtered — see vehicleEntriesQ comment for the
                limitation around guard-logged walk-in plates. */}
            {recentVehicleEntries.length > 0 ? (
              <div className="mt-vt-4 border-t border-vt-border pt-vt-3">
                <div className="mb-vt-2 text-vt-12 uppercase tracking-wide text-vt-text-soft">
                  Recent gate events
                </div>
                <div className="flex flex-col divide-y divide-vt-border">
                  {recentVehicleEntries.map((e) => (
                    <div key={e.id} className="flex items-center gap-vt-3 py-vt-2">
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-vt-13 text-vt-text">
                          {e.number_plate}{' '}
                          <span className="text-vt-12 text-vt-text-muted">
                            · {e.actor_kind}
                          </span>
                        </div>
                        <div className="truncate text-vt-12 text-vt-text-muted">
                          In: {formatDateTime(e.entered_at)}
                          {e.exited_at ? ` · Out: ${formatDateTime(e.exited_at)}` : ''}
                        </div>
                      </div>
                      <Badge variant={VEHICLE_ENTRY_STATUS_VARIANT[e.status]} className="capitalize">
                        {e.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </CardContent>
        </Card>

        {/* ── Section · Tenancy / Lease ──────────────────────────── */}
        {/* Renders only when this resident is a tenant OR appears in an
            active TenancyTransition. Sources lease financials from the
            transition row itself (lease_end_date / monthly_rent_inr /
            deposit_inr). NOC docs come from the transition's nested nocs. */}
        {resident.relation === 'tenant' || activeLease ? (
          <Card>
            <CardContent className="p-vt-5">
              <SectionTitle
                icon={<KeyRound className="h-4 w-4" strokeWidth={1.75} />}
                title="Tenancy / Lease"
                subtitle="Lease window, financials and NOCs issued under this tenancy"
                action={
                  <Button variant="link" size="sm" asChild>
                    <Link to="/move-in-out">Manage →</Link>
                  </Button>
                }
              />
              {transitionsQ.isLoading ? (
                <Skeleton className="h-24 w-full" />
              ) : transitionsQ.isError ? (
                <p className="text-vt-13 text-vt-alert">
                  {getErrorMessage(transitionsQ.error)}
                </p>
              ) : !activeLease ? (
                <p className="text-vt-13 text-vt-text-muted">
                  Tenant resident — no active lease record on file. Capture lease
                  start, monthly rent and deposit via Move-in/out.
                </p>
              ) : (
                <>
                  <div className="grid grid-cols-1 gap-vt-4 sm:grid-cols-2 md:grid-cols-3">
                    <LabelValue label="Lease start">
                      {activeLease.effective_date
                        ? formatDate(activeLease.effective_date)
                        : '—'}
                    </LabelValue>
                    <LabelValue label="Lease end">
                      {activeLease.lease_end_date
                        ? formatDate(activeLease.lease_end_date)
                        : '—'}
                    </LabelValue>
                    <LabelValue label="Monthly rent">
                      {inrShort(activeLease.monthly_rent_inr)}
                    </LabelValue>
                    <LabelValue label="Deposit">
                      {inrShort(activeLease.deposit_inr)}
                    </LabelValue>
                    <LabelValue label="Transition status">
                      <Badge variant={TRANSITION_STATUS_VARIANT[activeLease.status]} className="capitalize">
                        {activeLease.status.replace(/_/g, ' ')}
                      </Badge>
                    </LabelValue>
                    <LabelValue label="Notice given">
                      {activeLease.notice_date ? formatDate(activeLease.notice_date) : '—'}
                    </LabelValue>
                  </div>

                  {activeLease.nocs.length > 0 ? (
                    <div className="mt-vt-4 border-t border-vt-border pt-vt-3">
                      <div className="mb-vt-2 text-vt-12 uppercase tracking-wide text-vt-text-soft">
                        NOCs issued
                      </div>
                      <div className="flex flex-col divide-y divide-vt-border">
                        {activeLease.nocs.map((n) => (
                          <div key={n.id} className="flex items-center gap-vt-3 py-vt-2">
                            <div className="min-w-0 flex-1">
                              <div className="truncate text-vt-13 font-medium text-vt-text">
                                {n.noc_number}
                              </div>
                              <div className="truncate text-vt-12 text-vt-text-muted">
                                {NOC_PURPOSE_LABEL[n.purpose as NocPurpose] ?? n.purpose} ·{' '}
                                Issued {formatDate(n.issued_at)}
                              </div>
                            </div>
                            {n.revoked_at ? (
                              <Badge variant="alert">Revoked</Badge>
                            ) : (
                              <Badge variant="ok">Active</Badge>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </>
              )}
            </CardContent>
          </Card>
        ) : null}

        {/* ── Section · Amenity bookings ─────────────────────────── */}
        <Card>
          <CardContent className="p-vt-5">
            <SectionTitle
              icon={<Sparkles className="h-4 w-4" strokeWidth={1.75} />}
              title="Amenity bookings"
              subtitle="Last 5 facility reservations by this resident"
              action={
                <Button variant="link" size="sm" asChild>
                  <Link to="/amenities">See all →</Link>
                </Button>
              }
            />
            {bookingsQ.isLoading ? (
              <Skeleton className="h-24 w-full" />
            ) : bookingsQ.isError ? (
              <p className="text-vt-13 text-vt-alert">
                {getErrorMessage(bookingsQ.error)} —{' '}
                <button
                  type="button"
                  onClick={() => void bookingsQ.refetch()}
                  className="font-medium text-vt-primary hover:underline"
                >
                  retry
                </button>
              </p>
            ) : (bookingsQ.data?.items.length ?? 0) === 0 ? (
              <p className="text-vt-13 text-vt-text-muted">
                No amenity bookings on record for this resident.
              </p>
            ) : (
              <div className="flex flex-col divide-y divide-vt-border">
                {bookingsQ.data!.items.map((b) => (
                  <div key={b.id} className="flex items-center gap-vt-3 py-vt-2">
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-vt-13 font-medium text-vt-text">
                        {b.amenity.name}
                      </div>
                      <div className="truncate text-vt-12 text-vt-text-muted">
                        {formatDateTime(b.starts_at)} → {formatDateTime(b.ends_at)}
                        {b.fee_inr > 0 ? ` · ${fmtInr(b.fee_inr)}` : ''}
                      </div>
                    </div>
                    <Badge variant={BOOKING_STATUS_VARIANT[b.status]} className="capitalize">
                      {b.status.replace(/_/g, ' ')}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* ── Section 7 · Recent complaints ──────────────────────── */}
        <Card>
          <CardContent className="p-vt-5">
            <SectionTitle
              icon={<AlertTriangle className="h-4 w-4" strokeWidth={1.75} />}
              title="Recent complaints"
              subtitle="Last 5 tickets raised for this flat"
              action={
                <Button variant="link" size="sm" asChild>
                  <Link to="/helpdesk" search={{ unit_id: unitId } as never}>
                    See all →
                  </Link>
                </Button>
              }
            />
            {complaintsQ.isLoading ? (
              <Skeleton className="h-24 w-full" />
            ) : complaintsQ.isError ? (
              <p className="text-vt-13 text-vt-alert">{getErrorMessage(complaintsQ.error)}</p>
            ) : (complaintsQ.data?.items.length ?? 0) === 0 ? (
              <p className="text-vt-13 text-vt-text-muted">No complaints raised for this flat yet.</p>
            ) : (
              <div className="flex flex-col divide-y divide-vt-border">
                {complaintsQ.data!.items.map((c) => (
                  <div key={c.id} className="flex items-center gap-vt-3 py-vt-2">
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-vt-13 font-medium text-vt-text">{c.title}</div>
                      <div className="truncate text-vt-12 text-vt-text-muted">
                        <span className="capitalize">{c.category.replace(/_/g, ' ')}</span>
                        {' · '}
                        {formatDateTime(c.created_at)}
                      </div>
                    </div>
                    <Badge variant={COMPLAINT_STATUS_VARIANT[c.status]} className="capitalize">
                      {c.status.replace('_', ' ')}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* ── Section · Move-in / Move-out timeline ──────────────── */}
        {/* Pairs the resident row's move_in_date / move_out_date with any
            TenancyTransition events involving this resident — gives ops a
            single timeline for "when did they arrive / what's happening
            with their tenancy". */}
        <Card>
          <CardContent className="p-vt-5">
            <SectionTitle
              icon={<CalendarClock className="h-4 w-4" strokeWidth={1.75} />}
              title="Move-in / Move-out"
              subtitle="Onboarding dates + tenancy transition events"
              action={
                <Button variant="link" size="sm" asChild>
                  <Link to="/move-in-out">Manage transitions →</Link>
                </Button>
              }
            />
            <div className="grid grid-cols-1 gap-vt-4 sm:grid-cols-2">
              <LabelValue label="Move-in date">
                {resident.move_in_date ? formatDate(resident.move_in_date) : '—'}
              </LabelValue>
              <LabelValue label="Move-out date">
                {resident.move_out_date ? formatDate(resident.move_out_date) : '—'}
              </LabelValue>
            </div>
            {transitionsQ.isLoading ? (
              <Skeleton className="mt-vt-3 h-20 w-full" />
            ) : transitionsQ.isError ? (
              <p className="mt-vt-3 text-vt-13 text-vt-alert">
                {getErrorMessage(transitionsQ.error)} —{' '}
                <button
                  type="button"
                  onClick={() => void transitionsQ.refetch()}
                  className="font-medium text-vt-primary hover:underline"
                >
                  retry
                </button>
              </p>
            ) : involvedTransitions.length > 0 ? (
              <div className="mt-vt-4 border-t border-vt-border pt-vt-3">
                <div className="mb-vt-2 text-vt-12 uppercase tracking-wide text-vt-text-soft">
                  Transition events
                </div>
                <div className="flex flex-col divide-y divide-vt-border">
                  {involvedTransitions.slice(0, 5).map((t) => {
                    const role =
                      t.outgoing_resident_id === id
                        ? 'Outgoing'
                        : t.incoming_resident_id === id
                          ? 'Incoming'
                          : '—';
                    return (
                      <div key={t.id} className="flex items-center gap-vt-3 py-vt-2">
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-vt-13 font-medium text-vt-text">
                            {TRANSITION_KIND_LABEL[t.kind]} ·{' '}
                            <span className="text-vt-text-muted">{role}</span>
                          </div>
                          <div className="truncate text-vt-12 text-vt-text-muted">
                            Created {formatDateTime(t.created_at)}
                            {t.effective_date ? ` · Effective ${formatDate(t.effective_date)}` : ''}
                            {t.completed_at ? ` · Completed ${formatDate(t.completed_at)}` : ''}
                          </div>
                        </div>
                        <Badge variant={TRANSITION_STATUS_VARIANT[t.status]} className="capitalize">
                          {t.status.replace(/_/g, ' ')}
                        </Badge>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <p className="mt-vt-3 text-vt-13 text-vt-text-muted">
                No tenancy transitions on file involving this resident.
              </p>
            )}
          </CardContent>
        </Card>

        {/* ── Section · Community activity ───────────────────────── */}
        {/* /v1/community/posts has no author_id filter; we client-filter
            the first 50 visible posts. Reactions + comments are stored as
            denormalised counters on each post, so we surface them inline. */}
        <Card>
          <CardContent className="p-vt-5">
            <SectionTitle
              icon={<MessageSquare className="h-4 w-4" strokeWidth={1.75} />}
              title="Community activity"
              subtitle="Recent posts authored by this resident"
              action={
                <Button variant="link" size="sm" asChild>
                  <Link to="/community">Open feed →</Link>
                </Button>
              }
            />
            {communityQ.isLoading ? (
              <Skeleton className="h-24 w-full" />
            ) : communityQ.isError ? (
              <p className="text-vt-13 text-vt-alert">
                {getErrorMessage(communityQ.error)} —{' '}
                <button
                  type="button"
                  onClick={() => void communityQ.refetch()}
                  className="font-medium text-vt-primary hover:underline"
                >
                  retry
                </button>
              </p>
            ) : myCommunityPosts.length === 0 ? (
              <p className="text-vt-13 text-vt-text-muted">
                No community posts from this resident in the recent feed.
              </p>
            ) : (
              <div className="flex flex-col divide-y divide-vt-border">
                {myCommunityPosts.map((p) => (
                  <div key={p.id} className="flex items-start gap-vt-3 py-vt-2">
                    <div className="min-w-0 flex-1">
                      <div className="line-clamp-2 text-vt-13 text-vt-text">{p.body}</div>
                      <div className="mt-1 truncate text-vt-12 text-vt-text-muted">
                        <span className="capitalize">{p.category}</span>
                        {' · '}
                        {formatDateTime(p.created_at)}
                        {' · '}
                        {p.like_count} reaction{p.like_count === 1 ? '' : 's'}
                        {' · '}
                        {p.comment_count} comment{p.comment_count === 1 ? '' : 's'}
                      </div>
                    </div>
                    {p.status === 'hidden' ? (
                      <Badge variant="alert">Hidden</Badge>
                    ) : p.pinned ? (
                      <Badge variant="info">Pinned</Badge>
                    ) : null}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* ── Section · Notices ──────────────────────────────────── */}
        {/* /v1/notices doesn't accept unit_id. We pull the latest 50
            published notices and client-filter to society-wide + block-wide
            (matching this unit's block) + unit-targeted (matching this
            unit). Owners/tenants audience filter is approximated as
            society-wide until the API exposes the resident-level filter. */}
        <Card>
          <CardContent className="p-vt-5">
            <SectionTitle
              icon={<Bell className="h-4 w-4" strokeWidth={1.75} />}
              title="Notices"
              subtitle="Last 5 notices delivered to this unit"
              action={
                <Button variant="link" size="sm" asChild>
                  <Link to="/notices">See all →</Link>
                </Button>
              }
            />
            {noticesQ.isLoading ? (
              <Skeleton className="h-24 w-full" />
            ) : noticesQ.isError ? (
              <p className="text-vt-13 text-vt-alert">
                {getErrorMessage(noticesQ.error)} —{' '}
                <button
                  type="button"
                  onClick={() => void noticesQ.refetch()}
                  className="font-medium text-vt-primary hover:underline"
                >
                  retry
                </button>
              </p>
            ) : unitNoticeRows.length === 0 ? (
              <p className="text-vt-13 text-vt-text-muted">
                No published notices targeting this unit recently.
              </p>
            ) : (
              <div className="flex flex-col divide-y divide-vt-border">
                {unitNoticeRows.map((n) => (
                  <div key={n.id} className="flex items-start gap-vt-3 py-vt-2">
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-vt-13 font-medium text-vt-text">
                        {n.title}
                      </div>
                      <div className="truncate text-vt-12 text-vt-text-muted">
                        {NOTICE_CATEGORY_LABEL[n.category] ?? n.category}
                        {' · '}
                        <span className="capitalize">
                          {n.audience === 'all'
                            ? 'Society-wide'
                            : n.audience === 'block'
                              ? 'Block'
                              : n.audience === 'unit'
                                ? 'Unit'
                                : n.audience}
                        </span>
                        {' · '}
                        {n.published_at ? formatDate(n.published_at) : formatDate(n.created_at)}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      {n.pinned ? <Badge variant="info">Pinned</Badge> : null}
                      {typeof n.read_count === 'number' ? (
                        <Badge variant="muted">{n.read_count} read</Badge>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* ── Section · Requests (cross-cutting roll-up) ─────────── */}
        {/* Combines FV approval requests, NOCs, tenancy transitions, phone
            change events (audit log), DPDP erasure + export logs and
            pre-approvals into a single tabbed card. Each tab renders a
            small list from the already-fetched parent queries — no extra
            fetches.

            Caveat: the visitor list response doesn't currently expose the
            preapproved_by_resident_id field, so the "Pre-approvals" tab
            falls back to "approved visitors on this unit" until the API
            includes that attribution. */}
        <Card>
          <CardContent className="p-vt-5">
            <SectionTitle
              icon={<Inbox className="h-4 w-4" strokeWidth={1.75} />}
              title="Requests"
              subtitle="Approvals, NOCs, transitions, DPDP, phone-change, pre-approvals"
            />
            <div className="mb-vt-3 overflow-x-auto">
              <TabsPills
                size="sm"
                value={requestsTab}
                onChange={setRequestsTab}
                options={[
                  { value: 'all', label: 'All' },
                  {
                    value: 'fv',
                    label: 'FV approvals',
                    count: requestCounts.fv_approvals
                  },
                  { value: 'nocs', label: 'NOCs', count: requestCounts.nocs },
                  {
                    value: 'transitions',
                    label: 'Transitions',
                    count: requestCounts.transitions
                  },
                  {
                    value: 'phone',
                    label: 'Phone change',
                    count: requestCounts.phone_change
                  },
                  { value: 'dpdp', label: 'DPDP', count: requestCounts.dpdp },
                  {
                    value: 'preapprovals',
                    label: 'Pre-approvals',
                    count: requestCounts.preapprovals
                  }
                ]}
                aria-label="Filter requests by kind"
              />
            </div>

            <RequestsBody
              tab={requestsTab}
              residentId={id}
              fvApprovals={pendingFvApprovals}
              nocs={nocsQ.data?.items ?? []}
              transitions={involvedTransitions}
              phoneEvents={phoneChangeEvents}
              erasures={myErasures}
              exports={exportsQ.data?.items ?? []}
              preapprovals={preapprovals}
              loading={
                fvQ.isLoading ||
                nocsQ.isLoading ||
                transitionsQ.isLoading ||
                auditQ.isLoading ||
                erasureQ.isLoading ||
                exportsQ.isLoading ||
                visitorsQ.isLoading
              }
              errored={
                fvQ.isError ||
                nocsQ.isError ||
                transitionsQ.isError ||
                auditQ.isError ||
                erasureQ.isError ||
                exportsQ.isError
              }
            />
          </CardContent>
        </Card>

        {/* ── T3 Section · Vendor jobs ───────────────────────────── */}
        <Card>
          <CardContent className="p-vt-5">
            <SectionTitle
              icon={<Wrench className="h-4 w-4" strokeWidth={1.75} />}
              title="Vendor jobs"
              subtitle="Vendor invoices + complaints raised by this resident"
              action={
                <Button variant="link" size="sm" asChild>
                  <Link to="/helpdesk" search={{ resident_id: id } as never}>
                    Open helpdesk →
                  </Link>
                </Button>
              }
            />
            {vendorJobsQ.isLoading ? (
              <Skeleton className="h-24 w-full" />
            ) : vendorJobsQ.isError ? (
              <p className="text-vt-13 text-vt-alert">{getErrorMessage(vendorJobsQ.error)}</p>
            ) : (vendorJobsQ.data?.complaints.length ?? 0) === 0 &&
              (vendorJobsQ.data?.invoices.length ?? 0) === 0 ? (
              <p className="text-vt-13 text-vt-text-muted">
                No complaints or vendor invoices on file for this resident.
              </p>
            ) : (
              <div className="space-y-vt-4">
                {(vendorJobsQ.data?.complaints.length ?? 0) > 0 ? (
                  <div>
                    <div className="mb-vt-2 text-vt-12 uppercase tracking-wide text-vt-text-soft">
                      Complaints raised
                    </div>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Title</TableHead>
                          <TableHead>Category</TableHead>
                          <TableHead>Vendor</TableHead>
                          <TableHead>Raised</TableHead>
                          <TableHead className="text-right">Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {vendorJobsQ.data!.complaints.map((c) => (
                          <TableRow key={c.id}>
                            <TableCell className="text-vt-13 text-vt-text">{c.title}</TableCell>
                            <TableCell className="text-vt-12 text-vt-text-muted capitalize">
                              {c.category.replace(/_/g, ' ')}
                            </TableCell>
                            <TableCell className="text-vt-12 text-vt-text-muted">
                              {c.vendor_assignments[0]?.vendor?.name ?? '—'}
                            </TableCell>
                            <TableCell className="text-vt-12 text-vt-text-muted">
                              {formatDate(c.created_at)}
                            </TableCell>
                            <TableCell className="text-right">
                              <Badge
                                variant={COMPLAINT_STATUS_VARIANT[c.status]}
                                className="capitalize"
                              >
                                {c.status.replace(/_/g, ' ')}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : null}

                {(vendorJobsQ.data?.invoices.length ?? 0) > 0 ? (
                  <div>
                    <div className="mb-vt-2 text-vt-12 uppercase tracking-wide text-vt-text-soft">
                      Linked vendor invoices
                    </div>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Invoice #</TableHead>
                          <TableHead>Vendor</TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                          <TableHead className="text-right">Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {vendorJobsQ.data!.invoices.map((inv) => (
                          <TableRow key={inv.id}>
                            <TableCell className="text-vt-13 text-vt-text">
                              {inv.invoice_number}
                            </TableCell>
                            <TableCell className="text-vt-12 text-vt-text-muted">
                              {inv.vendor?.name ?? '—'}
                            </TableCell>
                            <TableCell className="text-vt-12 text-vt-text-muted">
                              {formatDate(inv.invoice_date)}
                            </TableCell>
                            <TableCell className="text-right text-vt-13 text-vt-text">
                              {fmtInr(inv.amount_inr)}
                            </TableCell>
                            <TableCell className="text-right">
                              <Badge
                                variant={inv.status === 'paid' ? 'ok' : 'warn'}
                                className="capitalize"
                              >
                                {inv.status}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : null}
              </div>
            )}
          </CardContent>
        </Card>

        {/* ── T3 Section · Governance role ───────────────────────── */}
        <Card>
          <CardContent className="p-vt-5">
            <SectionTitle
              icon={<Shield className="h-4 w-4" strokeWidth={1.75} />}
              title="Governance role"
              subtitle="Current admin role + history of role changes + past election wins"
            />
            {govRoleQ.isLoading ? (
              <Skeleton className="h-24 w-full" />
            ) : govRoleQ.isError ? (
              <p className="text-vt-13 text-vt-alert">{getErrorMessage(govRoleQ.error)}</p>
            ) : (
              <div className="space-y-vt-4">
                <div className="rounded-vt-md border border-vt-border bg-vt-surface-2 p-vt-4">
                  <div className="text-vt-12 uppercase tracking-wide text-vt-text-soft">
                    Current role
                  </div>
                  <div className="mt-vt-1 flex items-center gap-vt-2">
                    {govRoleQ.data?.current_role ? (
                      <Badge variant="info" className="text-vt-13">
                        {ADMIN_ROLE_LABEL[govRoleQ.data.current_role] ??
                          govRoleQ.data.current_role}
                      </Badge>
                    ) : (
                      <Badge variant="muted">No admin role</Badge>
                    )}
                    {govRoleQ.data?.admin_record_id ? (
                      govRoleQ.data.admin_active ? (
                        <Badge variant="ok">Active</Badge>
                      ) : (
                        <Badge variant="warn">Inactive</Badge>
                      )
                    ) : null}
                  </div>
                </div>

                <div>
                  <div className="mb-vt-2 text-vt-12 uppercase tracking-wide text-vt-text-soft">
                    Role changes
                  </div>
                  {(govRoleQ.data?.history.length ?? 0) === 0 ? (
                    <p className="text-vt-13 text-vt-text-muted">
                      No role-change events recorded.
                    </p>
                  ) : (
                    <div className="flex flex-col divide-y divide-vt-border">
                      {govRoleQ.data!.history.map((h) => (
                        <div key={h.id} className="flex items-center gap-vt-3 py-vt-2">
                          <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-vt-sm bg-vt-surface-2 text-vt-text-soft">
                            <Shield className="h-3.5 w-3.5" strokeWidth={1.75} />
                          </span>
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-vt-13 font-medium text-vt-text capitalize">
                              {h.action.replace(/_/g, ' ').replace(/\./g, ' · ')}
                            </div>
                            <div className="truncate text-vt-12 text-vt-text-muted">
                              {formatDateTime(h.at)} · by {h.actor_role}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <div className="mb-vt-2 text-vt-12 uppercase tracking-wide text-vt-text-soft">
                    Elections won
                  </div>
                  {(govRoleQ.data?.wins.length ?? 0) === 0 ? (
                    <p className="text-vt-13 text-vt-text-muted">
                      No past election wins for this resident.
                    </p>
                  ) : (
                    <div className="flex flex-col divide-y divide-vt-border">
                      {govRoleQ.data!.wins.map((w) => (
                        <div key={w.id} className="flex items-center gap-vt-3 py-vt-2">
                          <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-vt-sm bg-vt-primary-100 text-vt-primary">
                            <VoteIcon className="h-3.5 w-3.5" strokeWidth={1.75} />
                          </span>
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-vt-13 font-medium text-vt-text">
                              {w.election?.title ?? '—'}
                            </div>
                            <div className="truncate text-vt-12 text-vt-text-muted capitalize">
                              {w.election?.role ?? '—'} · {w.vote_count} votes ·{' '}
                              {formatDate(w.computed_at)}
                            </div>
                          </div>
                          <Badge variant="ok">Winner</Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* ── T3 Section · Login history ─────────────────────────── */}
        <Card>
          <CardContent className="p-vt-5">
            <SectionTitle
              icon={<LogIn className="h-4 w-4" strokeWidth={1.75} />}
              title="Login history"
              subtitle="Last 20 logins by this resident (IP + device + role at login)"
            />
            {loginHistoryQ.isLoading ? (
              <Skeleton className="h-24 w-full" />
            ) : loginHistoryQ.isError ? (
              <p className="text-vt-13 text-vt-alert">{getErrorMessage(loginHistoryQ.error)}</p>
            ) : (loginHistoryQ.data?.items.length ?? 0) === 0 ? (
              <p className="text-vt-13 text-vt-text-muted">No login activity on record.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>When</TableHead>
                    <TableHead>IP</TableHead>
                    <TableHead>Device</TableHead>
                    <TableHead className="text-right">Role</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loginHistoryQ.data!.items.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell className="text-vt-13 text-vt-text">
                        {formatDateTime(r.at)}
                      </TableCell>
                      <TableCell className="text-vt-12 text-vt-text-muted">
                        <span className="inline-flex items-center gap-1">
                          <Globe className="h-3 w-3" strokeWidth={1.75} />
                          {r.ip ?? '—'}
                        </span>
                      </TableCell>
                      <TableCell
                        className="text-vt-12 text-vt-text-muted"
                        title={r.user_agent ?? undefined}
                      >
                        {shortUa(r.user_agent)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Badge variant="muted" className="capitalize">
                          {r.actor_role.replace(/_/g, ' ')}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* ── T3 Section · SOS events ────────────────────────────── */}
        <Card className="border-[color:var(--vt-alert-200,#fecaca)]">
          <CardContent className="p-vt-5">
            <SectionTitle
              icon={<Siren className="h-4 w-4" strokeWidth={1.75} />}
              title="SOS events"
              subtitle="Panic alerts raised by this resident"
            />
            {sosEventsQ.isLoading ? (
              <Skeleton className="h-24 w-full" />
            ) : sosEventsQ.isError ? (
              <p className="text-vt-13 text-vt-alert">{getErrorMessage(sosEventsQ.error)}</p>
            ) : (sosEventsQ.data?.items.length ?? 0) === 0 ? (
              <p className="text-vt-13 text-vt-text-muted">No SOS events recorded.</p>
            ) : (
              <div className="flex flex-col gap-vt-3">
                {sosEventsQ.data!.items.map((evt) => {
                  const payload = (evt.payload ?? {}) as {
                    type?: string;
                    notes?: string;
                    lat?: number;
                    lng?: number;
                  };
                  return (
                    <div
                      key={evt.id}
                      className="rounded-vt-md border border-[color:var(--vt-alert-200,#fecaca)] bg-[color:var(--vt-alert-50,#fef2f2)] p-vt-3"
                    >
                      <div className="flex items-start justify-between gap-vt-2">
                        <div className="min-w-0">
                          <div className="text-vt-13 font-medium text-vt-text capitalize">
                            {payload.type ?? 'security'} alert
                          </div>
                          <div className="text-vt-12 text-vt-text-muted">
                            {formatDateTime(evt.at)}
                            {payload.lat !== undefined && payload.lng !== undefined
                              ? ` · ${payload.lat.toFixed(4)}, ${payload.lng.toFixed(4)}`
                              : ''}
                          </div>
                          {payload.notes ? (
                            <div className="mt-vt-1 text-vt-12 text-vt-text">
                              {payload.notes}
                            </div>
                          ) : null}
                        </div>
                        <Badge variant="alert">SOS</Badge>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* ── T3 Section · DPDP / Privacy ────────────────────────── */}
        <Card>
          <CardContent className="p-vt-5">
            <SectionTitle
              icon={<KeyRound className="h-4 w-4" strokeWidth={1.75} />}
              title="DPDP / privacy"
              subtitle="Data export requests + erasure requests + consent audit"
            />
            {privacyQ.isLoading ? (
              <Skeleton className="h-24 w-full" />
            ) : privacyQ.isError ? (
              <p className="text-vt-13 text-vt-alert">{getErrorMessage(privacyQ.error)}</p>
            ) : (
              <div className="space-y-vt-4">
                <div className="grid grid-cols-2 gap-vt-3 md:grid-cols-3">
                  <KpiTile
                    label="Data exports"
                    value={String(privacyQ.data?.exports.length ?? 0)}
                    variant="muted"
                  />
                  <KpiTile
                    label="Erasure requests"
                    value={String(privacyQ.data?.erasures.length ?? 0)}
                    variant={
                      (privacyQ.data?.erasures.length ?? 0) > 0 ? 'alert' : 'muted'
                    }
                  />
                  <KpiTile
                    label="Consent events"
                    value={String(privacyQ.data?.audit.length ?? 0)}
                    variant="muted"
                  />
                </div>

                {(privacyQ.data?.erasures.length ?? 0) > 0 ? (
                  <div>
                    <div className="mb-vt-2 text-vt-12 uppercase tracking-wide text-vt-text-soft">
                      Erasure requests
                    </div>
                    <div className="flex flex-col divide-y divide-vt-border">
                      {privacyQ.data!.erasures.map((e) => (
                        <div key={e.id} className="flex items-center gap-vt-3 py-vt-2">
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-vt-13 font-medium text-vt-text capitalize">
                              {e.reason.replace(/_/g, ' ')}
                            </div>
                            <div className="truncate text-vt-12 text-vt-text-muted">
                              Filed {formatDateTime(e.created_at)}
                              {e.reviewed_at ? ` · Reviewed ${formatDateTime(e.reviewed_at)}` : ''}
                            </div>
                          </div>
                          <Badge variant={ERASURE_STATUS_VARIANT[e.status]} className="capitalize">
                            {e.status}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}

                {(privacyQ.data?.exports.length ?? 0) > 0 ? (
                  <div>
                    <div className="mb-vt-2 text-vt-12 uppercase tracking-wide text-vt-text-soft">
                      Data exports
                    </div>
                    <div className="flex flex-col divide-y divide-vt-border">
                      {privacyQ.data!.exports.map((x) => (
                        <div key={x.id} className="flex items-center gap-vt-3 py-vt-2">
                          <Download
                            className="h-4 w-4 shrink-0 text-vt-text-soft"
                            strokeWidth={1.75}
                          />
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-vt-13 font-medium text-vt-text">
                              {x.format.toUpperCase()} export
                            </div>
                            <div className="truncate text-vt-12 text-vt-text-muted">
                              {formatDateTime(x.generated_at)}
                            </div>
                          </div>
                          <Badge variant="info">Export</Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}

                {(privacyQ.data?.audit.length ?? 0) === 0 &&
                (privacyQ.data?.erasures.length ?? 0) === 0 &&
                (privacyQ.data?.exports.length ?? 0) === 0 ? (
                  <p className="text-vt-13 text-vt-text-muted">
                    No DPDP activity recorded for this resident.
                  </p>
                ) : null}
              </div>
            )}
          </CardContent>
        </Card>

        {/* ── T3 Section · Admin notes & tags ────────────────────── */}
        <Card>
          <CardContent className="p-vt-5">
            <SectionTitle
              icon={<NotebookPen className="h-4 w-4" strokeWidth={1.75} />}
              title="Admin notes & tags"
              subtitle="Private notes (admin-only — not visible to resident)"
              action={
                <Button variant="secondary" size="sm" onClick={openAddNote}>
                  <Pencil className="h-4 w-4" /> Add note
                </Button>
              }
            />
            {adminNotesQ.isLoading ? (
              <Skeleton className="h-24 w-full" />
            ) : adminNotesQ.isError ? (
              <p className="text-vt-13 text-vt-alert">{getErrorMessage(adminNotesQ.error)}</p>
            ) : (adminNotesQ.data?.items.length ?? 0) === 0 ? (
              <p className="text-vt-13 text-vt-text-muted">
                No admin notes on file. Add one to capture context that should never reach the
                resident.
              </p>
            ) : (
              <div className="flex flex-col gap-vt-3">
                {adminNotesQ.data!.items.map((n) => (
                  <div
                    key={n.id}
                    className="rounded-vt-md border border-vt-border bg-vt-surface-2 p-vt-3"
                  >
                    <div className="flex items-start justify-between gap-vt-2">
                      <div className="min-w-0 flex-1">
                        <div className="whitespace-pre-wrap text-vt-13 text-vt-text">
                          {n.body}
                        </div>
                        <div className="mt-vt-1 text-vt-12 text-vt-text-muted">
                          {formatDateTime(n.created_at)}
                          {n.updated_at !== n.created_at
                            ? ` · edited ${formatDateTime(n.updated_at)}`
                            : ''}
                        </div>
                        {n.tags.length > 0 ? (
                          <div className="mt-vt-2 flex flex-wrap gap-1">
                            {n.tags.map((t) => (
                              <span
                                key={t}
                                className={`inline-flex items-center gap-1 rounded-full px-vt-2 py-0.5 text-vt-11 font-medium ${tagColorClass(t)}`}
                              >
                                <Tag className="h-3 w-3" strokeWidth={1.75} />
                                {t}
                              </span>
                            ))}
                          </div>
                        ) : null}
                      </div>
                      <div className="flex shrink-0 items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditNote(n)}
                          title="Edit note"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setPendingDeleteNoteId(n.id)}
                          title="Delete note"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* ── T3 Section · KYC docs ──────────────────────────────── */}
        <Card>
          <CardContent className="p-vt-5">
            <SectionTitle
              icon={<ContactRound className="h-4 w-4" strokeWidth={1.75} />}
              title="KYC documents"
              subtitle="ID proof / Aadhaar / PAN — admin-managed"
              action={
                kycQ.data ? (
                  kycQ.data.verified ? (
                    <Badge variant="ok">
                      <CheckCircle2 className="h-3 w-3" strokeWidth={1.75} />
                      Verified
                    </Badge>
                  ) : (
                    <Button
                      variant="primary"
                      size="sm"
                      disabled={
                        verifyKycMutation.isPending ||
                        !(kycQ.data.has_id_proof || kycQ.data.has_aadhaar || kycQ.data.has_pan)
                      }
                      onClick={() => verifyKycMutation.mutate()}
                    >
                      <CheckCircle2 className="h-4 w-4" /> Verify KYC
                    </Button>
                  )
                ) : null
              }
            />
            {kycQ.isLoading ? (
              <Skeleton className="h-32 w-full" />
            ) : kycQ.isError ? (
              <p className="text-vt-13 text-vt-alert">{getErrorMessage(kycQ.error)}</p>
            ) : (
              <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-3">
                <ResidentKycTile
                  title="ID proof"
                  description={
                    kycQ.data?.id_proof_type
                      ? `${kycQ.data.id_proof_type.toUpperCase()}${kycQ.data.id_proof_last4 ? ` · ••• ${kycQ.data.id_proof_last4}` : ''}`
                      : 'Passport / DL / Aadhaar / PAN'
                  }
                  icon={<ContactRound className="h-4 w-4" strokeWidth={1.75} />}
                  hasFile={Boolean(kycQ.data?.has_id_proof)}
                  previewUrl={idProofUrl}
                  uploading={uploadingKyc === 'id_proof'}
                  onUpload={(file) =>
                    uploadKycMutation.mutate({ docType: 'id_proof', file })
                  }
                />
                <ResidentKycTile
                  title="Aadhaar"
                  description="UIDAI Aadhaar card / e-Aadhaar PDF"
                  icon={<ContactRound className="h-4 w-4" strokeWidth={1.75} />}
                  hasFile={Boolean(kycQ.data?.has_aadhaar)}
                  previewUrl={aadhaarUrl}
                  uploading={uploadingKyc === 'aadhaar'}
                  onUpload={(file) => uploadKycMutation.mutate({ docType: 'aadhaar', file })}
                />
                <ResidentKycTile
                  title="PAN"
                  description="Income-tax PAN card"
                  icon={<ContactRound className="h-4 w-4" strokeWidth={1.75} />}
                  hasFile={Boolean(kycQ.data?.has_pan)}
                  previewUrl={panUrl}
                  uploading={uploadingKyc === 'pan'}
                  onUpload={(file) => uploadKycMutation.mutate({ docType: 'pan', file })}
                />
              </div>
            )}
            {kycQ.data?.verified && kycQ.data.verified_at ? (
              <p className="mt-vt-3 text-vt-12 text-vt-text-muted">
                Verified on {formatDateTime(kycQ.data.verified_at)}
              </p>
            ) : null}
          </CardContent>
        </Card>

        {/* ── Admin-note add/edit dialog ─────────────────────────── */}
        <Dialog
          open={noteDialogOpen}
          onOpenChange={(o) => {
            setNoteDialogOpen(o);
            if (!o) setEditingNote(null);
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingNote ? 'Edit admin note' : 'Add admin note'}</DialogTitle>
              <DialogDescription>
                Notes are visible to other admins only. Use tags for quick scanning (e.g.
                <em> defaulter, vip, complainant</em>).
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-vt-3 py-vt-2">
              <div className="space-y-vt-1">
                <Label htmlFor="note-body">Note</Label>
                <Textarea
                  id="note-body"
                  value={noteDraftBody}
                  onChange={(e) => setNoteDraftBody(e.target.value)}
                  placeholder="What should other admins know?"
                  rows={6}
                  maxLength={2000}
                />
                <div className="text-right text-vt-11 text-vt-text-muted">
                  {noteDraftBody.length}/2000
                </div>
              </div>
              <div className="space-y-vt-1">
                <Label htmlFor="note-tags">Tags (comma-separated)</Label>
                <Input
                  id="note-tags"
                  value={noteDraftTags}
                  onChange={(e) => setNoteDraftTags(e.target.value)}
                  placeholder="e.g. defaulter, vip, complainant"
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="ghost"
                onClick={() => setNoteDialogOpen(false)}
                disabled={createNoteMutation.isPending || updateNoteMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={submitNote}
                loading={createNoteMutation.isPending || updateNoteMutation.isPending}
              >
                {editingNote ? 'Save changes' : 'Add note'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ── Confirm delete note ────────────────────────────────── */}
        <ConfirmDialog
          open={Boolean(pendingDeleteNoteId)}
          onOpenChange={(o) => {
            if (!o) setPendingDeleteNoteId(null);
          }}
          title="Delete admin note?"
          description="This note will be permanently removed. Other admins won't be able to see it any more."
          confirmLabel="Delete"
          variant="danger"
          loading={deleteNoteMutation.isPending}
          onConfirm={() => {
            if (pendingDeleteNoteId) deleteNoteMutation.mutate(pendingDeleteNoteId);
          }}
        />
      </div>
    </>
  );
}

// ─── small subcomponents ──────────────────────────────────────────────

function ResidentKycTile({
  title,
  description,
  icon,
  hasFile,
  previewUrl,
  uploading,
  onUpload
}: {
  title: string;
  description?: string;
  icon: React.ReactNode;
  hasFile: boolean;
  previewUrl?: string | null;
  uploading: boolean;
  onUpload: (file: File) => void;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-vt-3 rounded-vt-md border border-vt-border bg-vt-surface-2 p-vt-4">
      <div className="flex items-start gap-vt-2">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-vt-sm bg-vt-primary-100 text-vt-primary">
          {icon}
        </span>
        <div className="min-w-0 flex-1">
          <div className="font-medium text-vt-text">{title}</div>
          {description ? (
            <div className="text-vt-12 text-vt-text-muted">{description}</div>
          ) : null}
        </div>
        {hasFile ? <Badge variant="ok">On file</Badge> : <Badge variant="muted">Missing</Badge>}
      </div>
      {previewUrl ? (
        <a
          href={previewUrl}
          target="_blank"
          rel="noreferrer"
          className="flex h-40 items-center justify-center overflow-hidden rounded-vt-md border border-vt-border bg-vt-surface"
        >
          <img
            src={previewUrl}
            alt={title}
            className="max-h-full max-w-full object-contain"
          />
        </a>
      ) : (
        <div className="flex h-20 items-center justify-center rounded-vt-md border border-dashed border-vt-border text-vt-12 text-vt-text-soft">
          No file uploaded yet
        </div>
      )}
      <label className="inline-flex cursor-pointer items-center gap-1 rounded-vt-sm border border-vt-border bg-vt-surface px-vt-3 py-vt-1 text-vt-12 text-vt-text hover:bg-vt-surface-2">
        <Upload className="h-3.5 w-3.5" />
        {uploading ? 'Uploading…' : hasFile ? 'Replace' : 'Upload'}
        <input
          type="file"
          className="hidden"
          accept="image/jpeg,image/png,image/webp,application/pdf"
          disabled={uploading}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) onUpload(f);
            e.currentTarget.value = '';
          }}
        />
      </label>
    </div>
  );
}

function KpiTile({
  label,
  value,
  variant
}: {
  label: string;
  value: string;
  variant: 'ok' | 'alert' | 'muted';
}): JSX.Element {
  const tone =
    variant === 'ok'
      ? 'text-vt-safe'
      : variant === 'alert'
        ? 'text-vt-alert'
        : 'text-vt-text';
  return (
    <div className="rounded-vt-md border border-vt-border bg-vt-surface-2 px-vt-3 py-vt-3">
      <div className="text-vt-12 uppercase tracking-wide text-vt-text-soft">{label}</div>
      <div className={`mt-0.5 text-vt-20 font-semibold ${tone}`}>{value}</div>
    </div>
  );
}

/**
 * RequestsBody — renders the active Requests tab. Splits into one row-list
 * per source. Keeps the parent component lean and lets us swap individual
 * tab renderers without touching the page chrome.
 */
function RequestsBody({
  tab,
  residentId,
  fvApprovals,
  nocs,
  transitions,
  phoneEvents,
  erasures,
  exports: exportsList,
  preapprovals,
  loading,
  errored
}: {
  tab: string;
  residentId: string;
  fvApprovals: FvAssignmentRow[];
  nocs: NocRow[];
  transitions: TransitionRow[];
  phoneEvents: AuditLogRow[];
  erasures: ErasureRow[];
  exports: DataExportLogRow[];
  preapprovals: VisitorRow[];
  loading: boolean;
  errored: boolean;
}): JSX.Element {
  if (loading) {
    return <Skeleton className="h-24 w-full" />;
  }
  if (errored) {
    return (
      <p className="text-vt-13 text-vt-alert">
        One or more request feeds failed to load. Refresh the page to retry.
      </p>
    );
  }

  const sections = [
    {
      key: 'fv',
      title: 'FV approvals pending resident',
      icon: <ShieldCheck className="h-4 w-4" strokeWidth={1.75} />,
      empty: 'No frequent-visitor approvals waiting on this resident.',
      rows: fvApprovals.map((a) => ({
        id: a.id,
        primary: a.frequent_visitor.full_name,
        secondary: `${a.frequent_visitor.kind.replace(/_/g, ' ')} · ${a.days_of_week ?? 'any day'}`,
        badge: <Badge variant="warn">Pending resident</Badge>
      }))
    },
    {
      key: 'nocs',
      title: 'NOCs raised / issued',
      icon: <FileSignature className="h-4 w-4" strokeWidth={1.75} />,
      empty: 'No NOCs issued for this resident.',
      rows: nocs.map((n) => ({
        id: n.id,
        primary: `${n.noc_number}`,
        secondary: `${NOC_PURPOSE_LABEL[n.purpose] ?? n.purpose} · Issued ${formatDate(n.issued_at)}`,
        badge: n.revoked_at ? (
          <Badge variant="alert">Revoked</Badge>
        ) : (
          <Badge variant="ok">Active</Badge>
        )
      }))
    },
    {
      key: 'transitions',
      title: 'Tenancy transitions',
      icon: <CalendarClock className="h-4 w-4" strokeWidth={1.75} />,
      empty: 'No tenancy transitions involving this resident.',
      rows: transitions.map((t) => ({
        id: t.id,
        primary: `${TRANSITION_KIND_LABEL[t.kind]} · ${
          t.outgoing_resident_id === residentId
            ? 'Outgoing'
            : t.incoming_resident_id === residentId
              ? 'Incoming'
              : '—'
        }`,
        secondary: `Created ${formatDateTime(t.created_at)}`,
        badge: (
          <Badge variant={TRANSITION_STATUS_VARIANT[t.status]} className="capitalize">
            {t.status.replace(/_/g, ' ')}
          </Badge>
        )
      }))
    },
    {
      key: 'phone',
      title: 'Phone-change events',
      icon: <Phone className="h-4 w-4" strokeWidth={1.75} />,
      empty: 'No phone-change activity recorded.',
      rows: phoneEvents.map((e) => ({
        id: e.id,
        primary: AUDIT_ACTION_LABEL[e.action] ?? e.action,
        secondary: formatDateTime(e.at),
        badge:
          e.action === 'resident.phone_change_confirm' ? (
            <Badge variant="ok">Confirmed</Badge>
          ) : (
            <Badge variant="warn">Requested</Badge>
          )
      }))
    },
    {
      key: 'dpdp',
      title: 'DPDP — exports & erasure',
      icon: <Trash2 className="h-4 w-4" strokeWidth={1.75} />,
      empty: 'No DPDP requests for this resident.',
      rows: [
        ...erasures.map((e) => ({
          id: `erase-${e.id}`,
          primary: `Erasure request · ${e.reason.replace(/_/g, ' ')}`,
          secondary: `Filed ${formatDateTime(e.created_at)}${
            e.reviewed_at ? ` · Reviewed ${formatDateTime(e.reviewed_at)}` : ''
          }`,
          badge: (
            <Badge variant={ERASURE_STATUS_VARIANT[e.status]} className="capitalize">
              {e.status}
            </Badge>
          )
        })),
        ...exportsList.map((x) => ({
          id: `export-${x.id}`,
          primary: `Data export · ${x.format.toUpperCase()}`,
          secondary: `Generated ${formatDateTime(x.generated_at)}`,
          badge: <Badge variant="info">Export</Badge>
        }))
      ]
    },
    {
      key: 'preapprovals',
      title: 'Pre-approvals issued',
      icon: <Ticket className="h-4 w-4" strokeWidth={1.75} />,
      empty: 'No visitor pre-approvals on record.',
      rows: preapprovals.map((v) => ({
        id: v.id,
        primary: v.visitor_name,
        secondary: `${v.visit_kind} · ${formatDateTime(v.expected_at)}`,
        badge: <Badge variant="info">Pre-approved</Badge>
      }))
    }
  ] as const;

  const visible = tab === 'all' ? sections : sections.filter((s) => s.key === tab);

  return (
    <div className="flex flex-col gap-vt-4">
      {visible.map((s) => (
        <div key={s.key}>
          <div className="mb-vt-2 flex items-center gap-vt-2 text-vt-12 uppercase tracking-wide text-vt-text-soft">
            <span className="inline-flex h-5 w-5 items-center justify-center rounded-vt-sm bg-vt-surface-2 text-vt-text-muted">
              {s.icon}
            </span>
            {s.title}
            <span className="ml-1 text-vt-text-muted normal-case tracking-normal">
              · {s.rows.length}
            </span>
          </div>
          {s.rows.length === 0 ? (
            <p className="text-vt-13 text-vt-text-muted">{s.empty}</p>
          ) : (
            <div className="flex flex-col divide-y divide-vt-border">
              {s.rows.map((row) => (
                <div key={row.id} className="flex items-center gap-vt-3 py-vt-2">
                  <ClipboardList
                    className="h-4 w-4 shrink-0 text-vt-text-soft"
                    strokeWidth={1.75}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-vt-13 font-medium text-vt-text">
                      {row.primary}
                    </div>
                    <div className="truncate text-vt-12 text-vt-text-muted">
                      {row.secondary}
                    </div>
                  </div>
                  {row.badge}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
